import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Html5Qrcode } from 'html5-qrcode';
import {
    GetStoredXfOperatorSession,
    SaveXfOperatorSession,
    ClearXfOperatorSession,
    AuthenticateXfDemoOperator,
    LoginXfRealOperator,
    GetXfScannerStations,
    GetXfScannerZones,
    CheckXfAccessScan,
    GetXfRecentAccessLogs,
    GetXfPublicConfig,
    UpdateXfAdminConfig,
    ResetXfParticipantDemo,
    CreateXfScannerHubConnection,
    GetXfStationProducts,
    LookupXfParticipantForScanner,
    RecordXfPosPurchase,
    RecordXfSponsorActivation,
    RedeemXfRewardCoupon,
    SimulateXfStock,
    GetOfflineScanQueue,
    EnqueueOfflineScan,
    ClearOfflineScanQueue,
    ProcessOfflineScanQueue
} from '../../utilities/BL/XoloFest';
import {
    getSharedAudioContext,
    playAccessGrantedTone,
    playAccessDeniedTone,
    playAntiPassbackWarningTone,
    playScannerBeepByCue
} from '../../utilities/XfAudioSynthesizer';
import { StationTilesModal } from './StationTilesModal';
import { ScannerDemoDrawer } from './ScannerDemoDrawer';

const OPERATIONAL_MODES = [
    {
        mode: 'ACCESS',
        label: '🚪 Puertas de Acceso',
        shortDesc: 'Control de Puertas, Zonas VIP/Cancha y Anti-Passback',
        accent: '#10b981'
    },
    {
        mode: 'POS_STORE',
        label: '🍔 Punto de Venta (POS)',
        shortDesc: 'Registro de Consumo ($ MXN), Carrito Táctil y Sello Comercial',
        accent: '#f59e0b'
    },
    {
        mode: 'ACTIVATION',
        label: '🎯 Activación de Patrocinador',
        shortDesc: 'Dinámicas de Marca (Caliente.mx / Nissan) y Sello Obligatorio',
        accent: '#dc2626'
    },
    {
        mode: 'REWARD_REDEEM',
        label: '🎁 Canje de Souvenir (#CANJE)',
        shortDesc: 'Validación de Cupón Dorado y Entrega Física de Souvenir',
        accent: '#eab308'
    }
];

const FALLBACK_STATIONS_CATALOG = [
    {
        stationCode: 'P1_ACCESO',
        stationName: 'Puerta 1 General (Check-In Principal)',
        stationType: 'ACCESS',
        zoneCode: 'GEN',
        zoneName: 'Explanada General Xolo Fest',
        allowReentry: false,
        allowedCategories: ['GENERAL', 'VIP', 'CANCHA'],
        capacityLimit: 5000,
        currentOccupancy: 0
    },
    {
        stationCode: 'VIP_DOOR',
        stationName: 'Acceso Lounge Hospitalidad VIP',
        stationType: 'ACCESS',
        zoneCode: 'VIP',
        zoneName: 'Lounge Hospitalidad VIP Caliente',
        allowReentry: true,
        allowedCategories: ['VIP', 'CANCHA'],
        capacityLimit: 400,
        currentOccupancy: 0
    },
    {
        stationCode: 'CANCHA_DOOR',
        stationName: 'Túnel Acceso Experiencia Cancha',
        stationType: 'ACCESS',
        zoneCode: 'CANCHA',
        zoneName: 'Experiencia Nivel Cancha Xolos',
        allowReentry: true,
        allowedCategories: ['CANCHA'],
        capacityLimit: 150,
        currentOccupancy: 0
    },
    {
        stationCode: 'STAND_XOLOSHOP',
        stationName: 'Stand Oficial XoloShop (Merchandising)',
        stationType: 'POS_STORE',
        zoneCode: 'FAN_ZONE',
        zoneName: 'Fan Zone & Merchandising',
        allowReentry: true,
        allowedCategories: ['GENERAL', 'VIP', 'CANCHA'],
        capacityLimit: 2500,
        currentOccupancy: 0,
        sponsorName: 'XoloShop Oficial'
    },
    {
        stationCode: 'STAND_TACOS',
        stationName: 'Tacos & Grill Perrón (Gastronomía)',
        stationType: 'POS_STORE',
        zoneCode: 'GASTRO',
        zoneName: 'Corredor Gastronómico',
        allowReentry: true,
        allowedCategories: ['GENERAL', 'VIP', 'CANCHA'],
        capacityLimit: 2000,
        currentOccupancy: 0,
        sponsorName: 'Gastronomía Xolo Fest'
    },
    {
        stationCode: 'ACT_CALIENTE',
        stationName: 'Tiro a Gol Caliente.mx',
        stationType: 'ACTIVATION',
        zoneCode: 'FAN_ZONE',
        zoneName: 'Fan Zone Patrocinadores',
        allowReentry: true,
        allowedCategories: ['GENERAL', 'VIP', 'CANCHA'],
        capacityLimit: 2500,
        currentOccupancy: 0,
        sponsorName: 'Caliente.mx'
    },
    {
        stationCode: 'ACT_NISSAN',
        stationName: 'Reto Precisión Nissan',
        stationType: 'ACTIVATION',
        zoneCode: 'FAN_ZONE',
        zoneName: 'Fan Zone Patrocinadores',
        allowReentry: true,
        allowedCategories: ['GENERAL', 'VIP', 'CANCHA'],
        capacityLimit: 2500,
        currentOccupancy: 0,
        sponsorName: 'Nissan'
    }
];

const FALLBACK_POS_PRODUCTS = {
    STAND_XOLOSHOP: [
        {
            productId: 101,
            stationCode: 'STAND_XOLOSHOP',
            productName: 'Jersey Oficial Xolos 2026',
            category: 'MERCH',
            price: 1499,
            emoji: '👕'
        },
        {
            productId: 102,
            stationCode: 'STAND_XOLOSHOP',
            productName: 'Gorra Edición Xolo Fest',
            category: 'MERCH',
            price: 450,
            emoji: '🧢'
        },
        {
            productId: 103,
            stationCode: 'STAND_XOLOSHOP',
            productName: 'Bufanda Rojinegra',
            category: 'MERCH',
            price: 280,
            emoji: '🧣'
        },
        {
            productId: 104,
            stationCode: 'STAND_XOLOSHOP',
            productName: 'Vaso Termo Coleccionable',
            category: 'MERCH',
            price: 190,
            emoji: '🥤'
        }
    ],
    STAND_TACOS: [
        {
            productId: 201,
            stationCode: 'STAND_TACOS',
            productName: 'Orden 3 Tacos Carne Asada',
            category: 'FOOD',
            price: 160,
            emoji: '🌮'
        },
        {
            productId: 202,
            stationCode: 'STAND_TACOS',
            productName: 'Burrito Perrón Especial',
            category: 'FOOD',
            price: 140,
            emoji: '🌯'
        },
        {
            productId: 203,
            stationCode: 'STAND_TACOS',
            productName: 'Cerveza Artesanal Doble',
            category: 'DRINK',
            price: 110,
            emoji: '🍺'
        },
        {
            productId: 204,
            stationCode: 'STAND_TACOS',
            productName: 'Refresco / Agua Mineral',
            category: 'DRINK',
            price: 45,
            emoji: '🥤'
        }
    ]
};

const SPONSOR_ACTIVATION_PRESETS = {
    ACT_CALIENTE: [
        {
            id: 'cal_3goles',
            label: '⚽ 3 Goles Anotados - Premio Gorra (100 pts)',
            activationName: 'Tiro a Gol Caliente.mx',
            resultSummary: '3 Goles Anotados - Premio Gorra',
            scoreValue: 100
        },
        {
            id: 'cal_2goles',
            label: '🥅 2 Goles Anotados - Sticker Oficial (75 pts)',
            activationName: 'Tiro a Gol Caliente.mx',
            resultSummary: '2 Goles Anotados - Sticker Oficial',
            scoreValue: 75
        },
        {
            id: 'cal_angulo',
            label: '🔥 Tiro al Ángulo Perfecto (150 pts)',
            activationName: 'Tiro a Gol Caliente.mx',
            resultSummary: 'Tiro al Ángulo Perfecto',
            scoreValue: 150
        },
        {
            id: 'cal_part',
            label: '👏 Participación Completada (50 pts)',
            activationName: 'Tiro a Gol Caliente.mx',
            resultSummary: 'Participación Completada',
            scoreValue: 50
        }
    ],
    ACT_NISSAN: [
        {
            id: 'nis_testdrive',
            label: '🚗 Test Drive Virtual Completado (100 pts)',
            activationName: 'Reto Precisión Nissan',
            resultSummary: 'Test Drive Virtual Completado',
            scoreValue: 100
        },
        {
            id: 'nis_circuito',
            label: '🏁 Circuito Precisión Exitoso - Llavero Nissan (120 pts)',
            activationName: 'Reto Precisión Nissan',
            resultSummary: 'Circuito Precisión Exitoso - Llavero Nissan',
            scoreValue: 120
        },
        {
            id: 'nis_trivia',
            label: '⚡ Trivia Eléctrica Superada (90 pts)',
            activationName: 'Reto Precisión Nissan',
            resultSummary: 'Trivia Eléctrica Superada',
            scoreValue: 90
        },
        {
            id: 'nis_part',
            label: '👏 Participación Completada (50 pts)',
            activationName: 'Reto Precisión Nissan',
            resultSummary: 'Participación Completada',
            scoreValue: 50
        }
    ]
};

const DEMO_OPERATOR_PROFILES = [
    {
        username: 'staff_puerta1',
        label: '🚪 staff_puerta1 (Puerta 1 General)',
        defaultStation: 'P1_ACCESO',
        defaultMode: 'ACCESS',
        role: 'Operador'
    },
    {
        username: 'staff_vip',
        label: '👑 staff_vip (Hospitalidad VIP)',
        defaultStation: 'VIP_DOOR',
        defaultMode: 'ACCESS',
        role: 'Operador'
    },
    {
        username: 'staff_cancha',
        label: '⚽ staff_cancha (Túnel Cancha)',
        defaultStation: 'CANCHA_DOOR',
        defaultMode: 'ACCESS',
        role: 'Operador'
    },
    {
        username: 'cajero_xoloshop',
        label: '🛍️ cajero_xoloshop (POS XoloShop)',
        defaultStation: 'STAND_XOLOSHOP',
        defaultMode: 'POS_STORE',
        role: 'Operador'
    },
    {
        username: 'promotor_caliente',
        label: '🎯 promotor_caliente (Activación Caliente.mx)',
        defaultStation: 'ACT_CALIENTE',
        defaultMode: 'ACTIVATION',
        role: 'Operador'
    },
    {
        username: 'staff_canje',
        label: '🎁 staff_canje (Carpa de Premios #CANJE)',
        defaultStation: 'REWARD_BOOTH',
        defaultMode: 'REWARD_REDEEM',
        role: 'Operador'
    }
];

const QUICK_SAMPLE_FOLIOS = [
    { folio: 'XOLO-0001', category: 'GENERAL', label: 'XOLO-0001 (GENERAL)' },
    { folio: 'XOLO-0002', category: 'GENERAL', label: 'XOLO-0002 (GENERAL)' },
    { folio: 'XOLO-0071', category: 'VIP', label: 'XOLO-0071 (VIP)' },
    { folio: 'XOLO-0091', category: 'CANCHA', label: 'XOLO-0091 (CANCHA)' },
    { folio: 'XOLO-9999', category: 'INVÁLIDO', label: 'XOLO-9999 (Falso)' }
];

const INACTIVITY_TIMEOUT_SECONDS = 600; // 10 minutos

const formatTimeLocal = (utcStr) => {
    if (!utcStr) return '--:--:--';
    try {
        const d = new Date(utcStr);
        if (isNaN(d.getTime())) return '--:--:--';
        return d.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch {
        return '--:--:--';
    }
};

const formatCurrencyMxn = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });
};

const getProductEmoji = (prod) => {
    if (prod?.emoji) return prod.emoji;
    const name = String(prod?.productName || '').toLowerCase();
    if (name.includes('jersey') || name.includes('playera')) return '👕';
    if (name.includes('gorra')) return '🧢';
    if (name.includes('bufanda')) return '🧣';
    if (name.includes('taco')) return '🌮';
    if (name.includes('burrito')) return '🌯';
    if (name.includes('cerveza')) return '🍺';
    if (name.includes('refresco') || name.includes('agua')) return '🥤';
    if (name.includes('vaso') || name.includes('termo')) return '🥤';
    return '🛍️';
};

const getSemaphoreBadge = (occupancy, limit, explicitStatus = null) => {
    const safeLimit = Number(limit || 0);
    const safeOcc = Number(occupancy || 0);
    const pct = safeLimit > 0 ? Math.min(100, Math.round((safeOcc / safeLimit) * 100)) : 0;

    const status = explicitStatus || (pct >= 95 ? 'RED' : pct >= 80 ? 'YELLOW' : 'GREEN');

    if (status === 'RED') {
        return {
            status: 'RED',
            pct,
            bg: '#dc2626',
            border: '#f87171',
            text: '#ffffff',
            label: '🔴 AFORO CRÍTICO (≥95%)'
        };
    }
    if (status === 'YELLOW') {
        return {
            status: 'YELLOW',
            pct,
            bg: '#d97706',
            border: '#fbbf24',
            text: '#ffffff',
            label: '🟡 AFORO PREVENTIVO (80-94%)'
        };
    }
    return {
        status: 'GREEN',
        pct,
        bg: '#059669',
        border: '#34d399',
        text: '#ffffff',
        label: '🟢 FLUJO LIBRE (<80%)'
    };
};

export const XoloFestScanner = () => {
    const navigate = useNavigate();

    // --- Estado de Autenticación y Bloqueo por Inactividad (Escenario 4) ---
    const [operatorSession, setOperatorSession] = useState(() => GetStoredXfOperatorSession());
    const [selectedDemoUser, setSelectedDemoUser] = useState('staff_puerta1');
    const [realUsername, setRealUsername] = useState('');
    const [realPassword, setRealPassword] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [inactivityLockMessage, setInactivityLockMessage] = useState('');
    const [secondsUntilLock, setSecondsUntilLock] = useState(INACTIVITY_TIMEOUT_SECONDS);

    // --- Modo Operativo Superior (HU-04 + HU-05) ---
    const [operationalMode, setOperationalMode] = useState('ACCESS');
    const [demoBarTab, setDemoBarTab] = useState('HU05'); // 'HU04' | 'HU05'

    // --- Estado de Estaciones, Zonas y Configuración ---
    const [allStations, setAllStations] = useState(FALLBACK_STATIONS_CATALOG);
    const [zones, setZones] = useState([]);
    const [selectedStationCode, setSelectedStationCode] = useState(
        () => GetStoredXfOperatorSession()?.selectedStationCode || 'P1_ACCESO'
    );
    const [config, setConfig] = useState(null);
    const [togglingPreReg, setTogglingPreReg] = useState(false);
    const [signalrConnected, setSignalrConnected] = useState(false);

    // --- Estado de Flujo Dual Flexible: Aficionado Identificado ---
    const [identifiedParticipant, setIdentifiedParticipant] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);

    // --- Estado de Punto de Venta POS_STORE (HU-05) ---
    const [stationProducts, setStationProducts] = useState(FALLBACK_POS_PRODUCTS.STAND_XOLOSHOP);
    const [productsLoading, setProductsLoading] = useState(false);
    const [posCart, setPosCart] = useState({}); // { [productId]: { product, quantity } }
    const [customAmount, setCustomAmount] = useState('');
    const [customConcept, setCustomConcept] = useState('');

    // --- Estado de Activación de Patrocinador ACTIVATION (HU-05) ---
    const [selectedPresetId, setSelectedPresetId] = useState('cal_3goles');
    const [activationTitleInput, setActivationTitleInput] = useState('Tiro a Gol Caliente.mx');
    const [activationResultInput, setActivationResultInput] = useState(
        '3 Goles Anotados - Premio Gorra'
    );
    const [activationScoreInput, setActivationScoreInput] = useState('100');

    // --- Estado de Captura (Cámara, Keyboard Wedge, Manual) ---
    const [manualInput, setManualInput] = useState('XOLO-0001');
    const [scanningInProgress, setScanningInProgress] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraFacingMode, setCameraFacingMode] = useState('environment');
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchOn, setTorchOn] = useState(false);
    const [cameraError, setCameraError] = useState('');
    const [wedgeLastBurst, setWedgeLastBurst] = useState('');

    // --- Estado de Resultado Visual y Acústico ---
    const [scanResult, setScanResult] = useState(null);
    const [concurrentTestResult, setConcurrentTestResult] = useState(null);
    const [audioMuted, setAudioMuted] = useState(false);
    const [demoDrawerOpen, setDemoDrawerOpen] = useState(false);
    const [showStationSelector, setShowStationSelector] = useState(false);
    const [showTopHeader, setShowTopHeader] = useState(false);
    const [showModeMenu, setShowModeMenu] = useState(false);
    const [auditLogsOpen, setAuditLogsOpen] = useState(false);
    const [filterLogsByStation, setFilterLogsByStation] = useState(false);
    const [recentLogs, setRecentLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    // --- Estado de Resiliencia Perimetral Offline (HU-07) ---
    const [isOnline, setIsOnline] = useState(() =>
        typeof navigator !== 'undefined' ? navigator.onLine !== false : true
    );
    const [isSimulatedOffline, setIsSimulatedOffline] = useState(false);
    const [offlineQueue, setOfflineQueue] = useState(() => GetOfflineScanQueue());
    const [syncingOfflineQueue, setSyncingOfflineQueue] = useState(false);
    const [syncSuccessBanner, setSyncSuccessBanner] = useState('');

    // --- Referencias para Cámara, Debounce y Keyboard Wedge ---
    const html5QrCodeRef = useRef(null);
    const lastCameraScanRef = useRef({ text: '', timestamp: 0 });
    const wedgeBufferRef = useRef('');
    const wedgeLastKeyTimeRef = useRef(0);
    const audioMutedRef = useRef(audioMuted);
    const selectedStationRef = useRef(selectedStationCode);
    const operatorSessionRef = useRef(operatorSession);
    const operationalModeRef = useRef(operationalMode);
    const isSimulatedOfflineRef = useRef(isSimulatedOffline);
    const handleUniversalScanRef = useRef(null);

    useEffect(() => {
        isSimulatedOfflineRef.current = isSimulatedOffline;
    }, [isSimulatedOffline]);

    useEffect(() => {
        audioMutedRef.current = audioMuted;
    }, [audioMuted]);

    useEffect(() => {
        selectedStationRef.current = selectedStationCode;
    }, [selectedStationCode]);

    useEffect(() => {
        operatorSessionRef.current = operatorSession;
    }, [operatorSession]);

    useEffect(() => {
        operationalModeRef.current = operationalMode;
    }, [operationalMode]);

    // --- Cálculo de Totales del Carrito POS ---
    const posCartItemsList = Object.values(posCart).filter((row) => row.quantity > 0);
    const posItemsTotal = posCartItemsList.reduce(
        (acc, row) => acc + Number(row.product.price || 0) * Number(row.quantity || 0),
        0
    );
    const parsedCustomAmount =
        customAmount !== '' && !isNaN(Number(customAmount)) && Number(customAmount) > 0
            ? Number(customAmount)
            : 0;
    const posGrandTotal = posItemsTotal + parsedCustomAmount;

    // --- Detener cámara de forma segura ---
    const stopCameraScanner = useCallback(async () => {
        const instance = html5QrCodeRef.current;
        if (instance) {
            try {
                if (instance.isScanning) {
                    await instance.stop();
                }
                instance.clear();
            } catch {
                // Ignorar error al limpiar instancia de cámara
            }
            html5QrCodeRef.current = null;
        }
        setCameraActive(false);
        setTorchSupported(false);
        setTorchOn(false);
    }, []);

    // --- Bloqueo de Dispositivo por Inactividad o Expiración JWT (Escenario 4) ---
    const triggerInactivityLock = useCallback(
        async (
            reason = 'Dispositivo bloqueado automáticamente por inactividad. Reautentica al operador para continuar.'
        ) => {
            await stopCameraScanner();
            ClearXfOperatorSession();
            setOperatorSession(null);
            setScanResult(null);
            setIdentifiedParticipant(null);
            setInactivityLockMessage(reason);
        },
        [stopCameraScanner]
    );

    const registerOperatorActivity = useCallback(() => {
        setSecondsUntilLock(INACTIVITY_TIMEOUT_SECONDS);
    }, []);

    // --- Temporizador de Inactividad ---
    useEffect(() => {
        if (!operatorSession?.token) return undefined;

        const handleUserInteraction = () => {
            setSecondsUntilLock(INACTIVITY_TIMEOUT_SECONDS);
        };

        window.addEventListener('pointerdown', handleUserInteraction, { passive: true });
        window.addEventListener('keydown', handleUserInteraction, { passive: true });

        const interval = setInterval(() => {
            setSecondsUntilLock((prev) => {
                if (prev <= 1) {
                    triggerInactivityLock(
                        '🔒 Sesión bloqueada por inactividad del dispositivo (Escenario 4). Por seguridad del control de accesos, inicia sesión nuevamente.'
                    );
                    return INACTIVITY_TIMEOUT_SECONDS;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            window.removeEventListener('pointerdown', handleUserInteraction);
            window.removeEventListener('keydown', handleUserInteraction);
            clearInterval(interval);
        };
    }, [operatorSession?.token, triggerInactivityLock]);

    // --- Carga de Estaciones, Zonas, Configuración y Bitácora ---
    const loadScannerContextData = useCallback(
        async (jwtToken, stationCodeForLogs = null) => {
            if (!jwtToken) return;

            const [stationsRes, zonesRes, configRes, logsRes] = await Promise.all([
                GetXfScannerStations(jwtToken),
                GetXfScannerZones(jwtToken),
                GetXfPublicConfig(),
                GetXfRecentAccessLogs(
                    jwtToken,
                    filterLogsByStation ? stationCodeForLogs || selectedStationRef.current : null,
                    25
                )
            ]);

            if (stationsRes.isUnauthorized || zonesRes.isUnauthorized || logsRes.isUnauthorized) {
                triggerInactivityLock(
                    '🔒 El token de sesión del operador expiró o no está autorizado (HTTP 401/403). Por favor reautentica tu terminal.'
                );
                return;
            }

            if (stationsRes.success && stationsRes.stations.length > 0) {
                setAllStations(stationsRes.stations);
            }
            if (zonesRes.success && zonesRes.zones.length > 0) {
                setZones(zonesRes.zones);
            }
            if (configRes.success && configRes.data) {
                setConfig(configRes.data);
            }
            if (logsRes.success) {
                setRecentLogs(logsRes.logs || []);
            }
        },
        [filterLogsByStation, triggerInactivityLock]
    );

    const refreshLogsAndZones = useCallback(
        async (jwtToken = null) => {
            const activeToken = jwtToken || operatorSessionRef.current?.token;
            if (!activeToken) return;

            setLogsLoading(true);
            const [zonesRes, stationsRes, logsRes] = await Promise.all([
                GetXfScannerZones(activeToken),
                GetXfScannerStations(activeToken),
                GetXfRecentAccessLogs(
                    activeToken,
                    filterLogsByStation ? selectedStationRef.current : null,
                    25
                )
            ]);
            setLogsLoading(false);

            if (zonesRes.isUnauthorized || logsRes.isUnauthorized) {
                triggerInactivityLock(
                    '🔒 Tu sesión de operador expiró. Reautentica el dispositivo para continuar.'
                );
                return;
            }

            if (zonesRes.success && zonesRes.zones.length > 0) {
                setZones(zonesRes.zones);
            }
            if (stationsRes.success && stationsRes.stations.length > 0) {
                setAllStations(stationsRes.stations);
            }
            if (logsRes.success) {
                setRecentLogs(logsRes.logs || []);
            }
        },
        [filterLogsByStation, triggerInactivityLock]
    );

    useEffect(() => {
        if (operatorSession?.token) {
            loadScannerContextData(operatorSession.token, selectedStationCode);
        }
    }, [operatorSession?.token, selectedStationCode, loadScannerContextData]);

    // --- Cargar Catálogo de Productos POS cuando se elige una estación POS_STORE ---
    const loadPosCatalogForStation = useCallback(
        async (stationCode, jwtToken = null) => {
            const activeToken = jwtToken || operatorSessionRef.current?.token;
            const fallbackList =
                FALLBACK_POS_PRODUCTS[stationCode] || FALLBACK_POS_PRODUCTS.STAND_XOLOSHOP;

            setStationProducts(fallbackList);
            if (!activeToken) return;

            setProductsLoading(true);
            const res = await GetXfStationProducts(stationCode, activeToken);
            setProductsLoading(false);

            if (res.isUnauthorized) {
                triggerInactivityLock(
                    '🔒 Sesión expirada al consultar el catálogo POS. Reautentica al operador.'
                );
                return;
            }

            if (res.success && Array.isArray(res.products) && res.products.length > 0) {
                setStationProducts(res.products);
            }
        },
        [triggerInactivityLock]
    );

    useEffect(() => {
        if (
            operationalMode === 'POS_STORE' &&
            (selectedStationCode === 'STAND_XOLOSHOP' || selectedStationCode === 'STAND_TACOS')
        ) {
            loadPosCatalogForStation(selectedStationCode);
        }
    }, [operationalMode, selectedStationCode, loadPosCatalogForStation]);

    // --- Conexión SignalR para telemetría en vivo ---
    useEffect(() => {
        if (!operatorSession?.token) return undefined;

        const hubInstance = CreateXfScannerHubConnection({
            onConnectionStateChanged: (connected) => {
                setSignalrConnected(connected);
            },
            onZoneOccupancyUpdated: (zonePayload) => {
                if (!zonePayload) return;
                setZones((prevZones) =>
                    prevZones.map((z) =>
                        z.zoneCode === zonePayload.zoneCode
                            ? {
                                  ...z,
                                  currentOccupancy:
                                      zonePayload.currentOccupancy ?? z.currentOccupancy,
                                  capacityLimit: zonePayload.capacityLimit ?? z.capacityLimit,
                                  occupancyPercentage:
                                      zonePayload.occupancyPercentage ?? z.occupancyPercentage,
                                  semaphoreStatus:
                                      zonePayload.semaphoreStatus ?? z.semaphoreStatus
                              }
                            : z
                    )
                );
                setAllStations((prevStations) =>
                    prevStations.map((st) =>
                        st.zoneCode === zonePayload.zoneCode
                            ? {
                                  ...st,
                                  currentOccupancy:
                                      zonePayload.currentOccupancy ?? st.currentOccupancy,
                                  capacityLimit: zonePayload.capacityLimit ?? st.capacityLimit
                              }
                            : st
                    )
                );
            },
            onConfigUpdated: (newCfg) => {
                if (newCfg) {
                    setConfig((prev) => ({ ...(prev || {}), ...newCfg }));
                }
            },
            onStampAdded: () => {
                refreshLogsAndZones();
            },
            onParticipantReset: () => {
                setIdentifiedParticipant(null);
                refreshLogsAndZones();
            }
        });

        return () => {
            hubInstance.stop();
        };
    }, [operatorSession?.token, refreshLogsAndZones]);

    // --- Desmontaje seguro de cámara ---
    useEffect(() => {
        return () => {
            if (html5QrCodeRef.current) {
                try {
                    if (html5QrCodeRef.current.isScanning) {
                        html5QrCodeRef.current.stop().catch(() => {});
                    }
                    html5QrCodeRef.current.clear();
                } catch {
                    // Silencioso
                }
            }
        };
    }, []);

    // ========================================================================
    // RESILIENCIA PERIMETRAL OFFLINE Y COLA DE SINCRONIZACIÓN (HU-07)
    // ========================================================================
    const handleSyncOfflineQueue = useCallback(
        async (overrideToken = null) => {
            const activeToken = overrideToken || operatorSessionRef.current?.token;
            if (!activeToken) return null;

            const currentQueue = GetOfflineScanQueue();
            if (!currentQueue.length) {
                setOfflineQueue([]);
                return null;
            }

            setSyncingOfflineQueue(true);
            const syncResult = await ProcessOfflineScanQueue(activeToken);
            setSyncingOfflineQueue(false);

            const updatedQueue = GetOfflineScanQueue();
            setOfflineQueue(updatedQueue);

            if (syncResult.syncedCount > 0) {
                setSyncSuccessBanner(
                    `✅ ${syncResult.syncedCount} escaneos sincronizados exitosamente`
                );
                if (!audioMutedRef.current) {
                    playAccessGrantedTone();
                }
                refreshLogsAndZones(activeToken);
            }

            return syncResult;
        },
        [refreshLogsAndZones]
    );

    useEffect(() => {
        if (!syncSuccessBanner) return undefined;
        const timer = setTimeout(() => {
            setSyncSuccessBanner('');
        }, 6000);
        return () => clearTimeout(timer);
    }, [syncSuccessBanner]);

    useEffect(() => {
        const handleBrowserOnline = () => {
            setIsOnline(true);
            if (!isSimulatedOfflineRef.current) {
                handleSyncOfflineQueue();
            }
        };
        const handleBrowserOffline = () => {
            setIsOnline(false);
        };

        window.addEventListener('online', handleBrowserOnline);
        window.addEventListener('offline', handleBrowserOffline);

        return () => {
            window.removeEventListener('online', handleBrowserOnline);
            window.removeEventListener('offline', handleBrowserOffline);
        };
    }, [handleSyncOfflineQueue]);

    const handleToggleSimulateOffline = useCallback(async () => {
        const nextOffline = !isSimulatedOfflineRef.current;
        isSimulatedOfflineRef.current = nextOffline;
        setIsSimulatedOffline(nextOffline);

        if (nextOffline) {
            setSyncSuccessBanner('');
            if (!audioMutedRef.current) {
                playAntiPassbackWarningTone();
            }
        } else if (typeof navigator === 'undefined' || navigator.onLine !== false) {
            await handleSyncOfflineQueue();
        }
    }, [handleSyncOfflineQueue]);

    // ========================================================================
    // OPERACIONES DEL SCANNER (HU-04: ACCESO | HU-05: POS, ACTIVACIÓN, CANJE)
    // ========================================================================

    // 1. Validar Acceso en Puerta (HU-04 + Resiliencia Offline HU-07)
    const executeAccessScan = useCallback(
        async (rawFolioOrToken, scanMethod = 'MANUAL', overrideStationCode = null) => {
            const activeSession = operatorSessionRef.current;
            if (!activeSession?.token) {
                triggerInactivityLock(
                    '🔒 Debes iniciar sesión como operador para validar boletos en puerta.'
                );
                return null;
            }

            const cleanValue = String(rawFolioOrToken || '').trim();
            if (!cleanValue) return null;

            const targetStation = overrideStationCode || selectedStationRef.current || 'P1_ACCESO';

            getSharedAudioContext();
            registerOperatorActivity();
            setScanningInProgress(true);
            setConcurrentTestResult(null);

            const isCurrentlyOffline =
                (typeof navigator !== 'undefined' && navigator.onLine === false) ||
                isSimulatedOfflineRef.current;

            const enqueueAndNotifyOffline = () => {
                const clientScanId = `offline_${Date.now()}_${Math.random()
                    .toString(36)
                    .substring(2, 9)}`;
                const updatedQueue = EnqueueOfflineScan({
                    clientScanId,
                    ticketFolioOrToken: cleanValue,
                    stationCode: targetStation,
                    scanMethod,
                    queuedAtUtc: new Date().toISOString()
                });
                setOfflineQueue(updatedQueue);

                if (!audioMutedRef.current) {
                    playAntiPassbackWarningTone();
                }

                const offlineResult = {
                    success: true,
                    operationType: 'ACCESS',
                    status: 'QUEUED_OFFLINE',
                    colorTheme: 'YELLOW',
                    audioCue: 'WARNING_DOUBLE_PULSE',
                    title: '📶 ESCANEO ENCOLADO EN MODO OFFLINE',
                    message: `Sin conexión al servidor perimetral. El boleto ${cleanValue} fue almacenado en la cola local (${updatedQueue.length} pendientes) con ID idempotente ${clientScanId} para sincronización automática al reconectar.`,
                    ticketFolio: cleanValue,
                    stationCode: targetStation,
                    clientScanId,
                    scannedInput: cleanValue,
                    scanMethod,
                    evaluatedAtLocal: new Date().toLocaleTimeString('es-MX', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    })
                };

                setScanResult(offlineResult);
                return offlineResult;
            };

            if (isCurrentlyOffline) {
                setScanningInProgress(false);
                return enqueueAndNotifyOffline();
            }

            const clientScanId = `scan_${Date.now()}_${Math.random()
                .toString(36)
                .substring(2, 9)}`;

            const result = await CheckXfAccessScan(
                {
                    ticketFolioOrToken: cleanValue,
                    stationCode: targetStation,
                    scanMethod,
                    clientScanId
                },
                activeSession.token
            );

            setScanningInProgress(false);

            if (result.status === 'NETWORK_ERROR') {
                return enqueueAndNotifyOffline();
            }

            if (result.isUnauthorized) {
                triggerInactivityLock(
                    '🔒 Tu sesión de operador expiró (HTTP 401/403). Inicia sesión nuevamente.'
                );
                return null;
            }

            if (!audioMutedRef.current) {
                playScannerBeepByCue(result.audioCue || result.status || result.colorTheme);
            }

            setScanResult({
                ...result,
                operationType: 'ACCESS',
                scannedInput: cleanValue,
                scanMethod,
                evaluatedAtLocal: new Date().toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            });

            if (result.zoneCode && typeof result.zoneCurrentOccupancy === 'number') {
                setZones((prev) =>
                    prev.map((z) =>
                        z.zoneCode === result.zoneCode
                            ? {
                                  ...z,
                                  currentOccupancy: result.zoneCurrentOccupancy,
                                  capacityLimit: result.zoneCapacityLimit || z.capacityLimit
                              }
                            : z
                    )
                );
            }

            refreshLogsAndZones(activeSession.token);
            return result;
        },
        [registerOperatorActivity, refreshLogsAndZones, triggerInactivityLock]
    );

    // 2. Identificar Aficionado para Flujo Dual (Orden 2: Escaneo Primero -> Operar Después)
    const executeParticipantLookup = useCallback(
        async (rawQuery) => {
            const activeSession = operatorSessionRef.current;
            if (!activeSession?.token) return null;

            const cleanQuery = String(rawQuery || '').trim();
            if (!cleanQuery) return null;

            getSharedAudioContext();
            registerOperatorActivity();
            setLookupLoading(true);

            const res = await LookupXfParticipantForScanner(cleanQuery, activeSession.token);
            setLookupLoading(false);

            if (res.isUnauthorized) {
                triggerInactivityLock('🔒 Sesión de operador expirada.');
                return null;
            }

            if (!res.success) {
                if (!audioMutedRef.current) {
                    playAccessDeniedTone();
                }
                setScanResult({
                    success: false,
                    operationType: 'LOOKUP',
                    status: 'INVALID_TICKET',
                    colorTheme: 'RED',
                    audioCue: 'ERROR_BUZZER',
                    title: 'BOLETO NO ENCONTRADO',
                    message: res.message || 'No se encontró un boleto válido con ese código.',
                    scannedInput: cleanQuery,
                    evaluatedAtLocal: new Date().toLocaleTimeString('es-MX')
                });
                return null;
            }

            if (!audioMutedRef.current) {
                playAccessGrantedTone();
            }

            setIdentifiedParticipant(res);
            setManualInput(res.ticketFolio || cleanQuery);
            return res;
        },
        [registerOperatorActivity, triggerInactivityLock]
    );

    // 3. Registrar Compra en Punto de Venta POS_STORE (HU-05)
    const executePosPurchase = useCallback(
        async (
            rawFolioOrToken,
            scanMethod = 'MANUAL',
            overrideStationCode = null,
            overrideItems = null,
            overrideCustomAmount = null,
            overrideCustomConcept = null
        ) => {
            const activeSession = operatorSessionRef.current;
            if (!activeSession?.token) {
                triggerInactivityLock('🔒 Inicia sesión como operador para registrar ventas POS.');
                return null;
            }

            const targetFolio = String(
                rawFolioOrToken || identifiedParticipant?.ticketFolio || manualInput || ''
            ).trim();
            if (!targetFolio) return null;

            const targetStation =
                overrideStationCode ||
                (selectedStationRef.current?.startsWith('STAND_')
                    ? selectedStationRef.current
                    : 'STAND_XOLOSHOP');

            const itemsPayload =
                overrideItems !== null
                    ? overrideItems
                    : posCartItemsList.map((row) => ({
                          productId: row.product.productId || null,
                          productName: row.product.productName,
                          quantity: row.quantity,
                          unitPrice: Number(row.product.price || 0)
                      }));

            const finalCustomAmount =
                overrideCustomAmount !== null ? overrideCustomAmount : parsedCustomAmount || null;
            const finalCustomConcept =
                overrideCustomConcept !== null ? overrideCustomConcept : customConcept || null;

            getSharedAudioContext();
            registerOperatorActivity();
            setScanningInProgress(true);
            setConcurrentTestResult(null);

            const result = await RecordXfPosPurchase(
                {
                    ticketFolioOrToken: targetFolio,
                    stationCode: targetStation,
                    items: itemsPayload,
                    customAmount: finalCustomAmount,
                    customConcept: finalCustomConcept,
                    scanMethod
                },
                activeSession.token
            );

            setScanningInProgress(false);

            if (result.isUnauthorized) {
                triggerInactivityLock('🔒 Sesión de operador expirada.');
                return null;
            }

            if (!audioMutedRef.current) {
                playScannerBeepByCue(result.audioCue || result.colorTheme);
            }

            setScanResult({
                ...result,
                operationType: 'POS_STORE',
                scannedInput: targetFolio,
                scanMethod,
                evaluatedAtLocal: new Date().toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            });

            if (result.success) {
                // Limpiar carrito tras venta exitosa y actualizar tarjeta de aficionado si estaba fijado
                setPosCart({});
                setCustomAmount('');
                setCustomConcept('');
                if (identifiedParticipant) {
                    setIdentifiedParticipant((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  totalSpentAmount:
                                      result.newTotalSpentAmount ?? prev.totalSpentAmount,
                                  totalPurchasesCount:
                                      result.newTotalPurchasesCount ?? prev.totalPurchasesCount,
                                  hasEarnedReward:
                                      result.rewardEvaluation?.hasEarnedReward ??
                                      prev.hasEarnedReward,
                                  rewardCouponCode:
                                      result.rewardEvaluation?.rewardCouponCode ||
                                      prev.rewardCouponCode
                              }
                            : null
                    );
                }
            }

            refreshLogsAndZones(activeSession.token);
            return result;
        },
        [
            customConcept,
            identifiedParticipant,
            manualInput,
            parsedCustomAmount,
            posCartItemsList,
            registerOperatorActivity,
            refreshLogsAndZones,
            triggerInactivityLock
        ]
    );

    // 4. Registrar Activación de Patrocinador ACTIVATION (HU-05)
    const executeSponsorActivation = useCallback(
        async (
            rawFolioOrToken,
            scanMethod = 'MANUAL',
            overrideStationCode = null,
            overrideActivationName = null,
            overrideResultSummary = null,
            overrideScoreValue = null
        ) => {
            const activeSession = operatorSessionRef.current;
            if (!activeSession?.token) {
                triggerInactivityLock(
                    '🔒 Inicia sesión como operador para sellar activaciones de marca.'
                );
                return null;
            }

            const targetFolio = String(
                rawFolioOrToken || identifiedParticipant?.ticketFolio || manualInput || ''
            ).trim();
            if (!targetFolio) return null;

            const targetStation =
                overrideStationCode ||
                (selectedStationRef.current?.startsWith('ACT_')
                    ? selectedStationRef.current
                    : 'ACT_CALIENTE');

            const actName =
                overrideActivationName !== null ? overrideActivationName : activationTitleInput;
            const resSummary =
                overrideResultSummary !== null
                    ? overrideResultSummary
                    : activationResultInput || 'Participación Completada';
            const scoreVal =
                overrideScoreValue !== null
                    ? overrideScoreValue
                    : activationScoreInput !== ''
                    ? Number(activationScoreInput)
                    : 100;

            getSharedAudioContext();
            registerOperatorActivity();
            setScanningInProgress(true);
            setConcurrentTestResult(null);

            const result = await RecordXfSponsorActivation(
                {
                    ticketFolioOrToken: targetFolio,
                    stationCode: targetStation,
                    activationName: actName,
                    resultSummary: resSummary,
                    scoreValue: scoreVal,
                    scanMethod
                },
                activeSession.token
            );

            setScanningInProgress(false);

            if (result.isUnauthorized) {
                triggerInactivityLock('🔒 Sesión de operador expirada.');
                return null;
            }

            if (!audioMutedRef.current) {
                playScannerBeepByCue(result.audioCue || result.colorTheme);
            }

            setScanResult({
                ...result,
                operationType: 'ACTIVATION',
                scannedInput: targetFolio,
                scanMethod,
                evaluatedAtLocal: new Date().toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            });

            if (result.success && identifiedParticipant) {
                setIdentifiedParticipant((prev) =>
                    prev
                        ? {
                              ...prev,
                              completedActivationsCount:
                                  result.newCompletedActivationsCount ??
                                  prev.completedActivationsCount,
                              hasEarnedReward:
                                  result.rewardEvaluation?.hasEarnedReward ?? prev.hasEarnedReward,
                              rewardCouponCode:
                                  result.rewardEvaluation?.rewardCouponCode || prev.rewardCouponCode
                          }
                        : null
                );
            }

            refreshLogsAndZones(activeSession.token);
            return result;
        },
        [
            activationResultInput,
            activationScoreInput,
            activationTitleInput,
            identifiedParticipant,
            manualInput,
            registerOperatorActivity,
            refreshLogsAndZones,
            triggerInactivityLock
        ]
    );

    // 5. Canjear Souvenir Físico REWARD_REDEEM (#CANJE-XXXXXX / Folio / QR) (HU-05)
    const executeRewardRedemption = useCallback(
        async (rawCouponOrFolioOrToken, scanMethod = 'MANUAL') => {
            const activeSession = operatorSessionRef.current;
            if (!activeSession?.token) {
                triggerInactivityLock(
                    '🔒 Inicia sesión como operador para validar y entregar souvenirs.'
                );
                return null;
            }

            const cleanCode = String(
                rawCouponOrFolioOrToken ||
                    identifiedParticipant?.rewardCouponCode ||
                    identifiedParticipant?.ticketFolio ||
                    manualInput ||
                    ''
            ).trim();
            if (!cleanCode) return null;

            getSharedAudioContext();
            registerOperatorActivity();
            setScanningInProgress(true);
            setConcurrentTestResult(null);

            const result = await RedeemXfRewardCoupon(
                {
                    ticketFolioOrCouponOrToken: cleanCode,
                    stationCode: 'REWARD_BOOTH',
                    scanMethod
                },
                activeSession.token
            );

            setScanningInProgress(false);

            if (result.isUnauthorized) {
                triggerInactivityLock('🔒 Sesión de operador expirada.');
                return null;
            }

            if (!audioMutedRef.current) {
                playScannerBeepByCue(result.audioCue || result.colorTheme);
            }

            setScanResult({
                ...result,
                operationType: 'REWARD_REDEEM',
                scannedInput: cleanCode,
                scanMethod,
                evaluatedAtLocal: new Date().toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            });

            if (identifiedParticipant) {
                setIdentifiedParticipant((prev) =>
                    prev
                        ? {
                              ...prev,
                              hasRedeemedReward: Boolean(
                                  result.hasRedeemedReward ?? prev.hasRedeemedReward
                              )
                          }
                        : null
                );
            }

            refreshLogsAndZones(activeSession.token);
            return result;
        },
        [
            identifiedParticipant,
            manualInput,
            registerOperatorActivity,
            refreshLogsAndZones,
            triggerInactivityLock
        ]
    );

    // --- Despachador Universal al Escanear con Cámara, Pistola USB/BT o Input Manual (Flujo Dual Flexible) ---
    const handleUniversalScanTrigger = useCallback(
        async (scannedValue, scanMethod = 'MANUAL') => {
            const cleanCode = String(scannedValue || '').trim();
            if (!cleanCode) return;

            const currentMode = operationalModeRef.current;

            if (currentMode === 'ACCESS') {
                await executeAccessScan(cleanCode, scanMethod);
                return;
            }

            if (currentMode === 'POS_STORE') {
                // Flujo Dual Flexible:
                // - Orden 1: Si el operador ya armó el carrito (posGrandTotal > 0), registra la compra en 1 solo paso.
                // - Orden 2: Si el carrito aún está vacío (posGrandTotal === 0), identifica y fija al aficionado en pantalla.
                if (posGrandTotal > 0) {
                    await executePosPurchase(cleanCode, scanMethod);
                } else {
                    await executeParticipantLookup(cleanCode);
                }
                return;
            }

            if (currentMode === 'ACTIVATION') {
                // En Activación, si hay un resultado seleccionado se sella directamente al escanear
                await executeSponsorActivation(cleanCode, scanMethod);
                return;
            }

            if (currentMode === 'REWARD_REDEEM') {
                await executeRewardRedemption(cleanCode, scanMethod);
            }
        },
        [
            executeAccessScan,
            executeParticipantLookup,
            executePosPurchase,
            executeRewardRedemption,
            executeSponsorActivation,
            posGrandTotal
        ]
    );

    useEffect(() => {
        handleUniversalScanRef.current = handleUniversalScanTrigger;
    }, [handleUniversalScanTrigger]);

    // --- Listener Global de Pistola Lectora USB / Bluetooth (Keyboard Wedge) ---
    useEffect(() => {
        if (!operatorSession?.token) return undefined;

        const handleGlobalKeyDown = (e) => {
            const activeTag = document.activeElement?.tagName?.toUpperCase();
            if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') {
                return;
            }

            const now = Date.now();
            const delta = now - wedgeLastKeyTimeRef.current;

            if (delta > 120) {
                wedgeBufferRef.current = '';
            }
            wedgeLastKeyTimeRef.current = now;

            if (e.key === 'Enter') {
                const capturedCode = wedgeBufferRef.current.trim();
                wedgeBufferRef.current = '';
                if (capturedCode.length >= 4) {
                    e.preventDefault();
                    setWedgeLastBurst(capturedCode);
                    setManualInput(capturedCode);
                    if (handleUniversalScanRef.current) {
                        handleUniversalScanRef.current(capturedCode, 'KEYBOARD_WEDGE');
                    }
                }
                return;
            }

            if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                wedgeBufferRef.current += e.key;
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [operatorSession?.token]);

    // --- Iniciar / Cambiar Cámara con Html5Qrcode ---
    const startCameraScanner = useCallback(
        async (desiredFacingMode = cameraFacingMode) => {
            setCameraError('');
            getSharedAudioContext();

            try {
                await stopCameraScanner();

                const qrInstance = new Html5Qrcode('xf-qr-reader-viewport');
                html5QrCodeRef.current = qrInstance;

                await qrInstance.start(
                    { facingMode: desiredFacingMode },
                    {
                        fps: 12,
                        qrbox: { width: 250, height: 250 },
                        aspectRatio: 1.0
                    },
                    (decodedText) => {
                        const cleanText = String(decodedText || '').trim();
                        if (!cleanText) return;

                        const now = Date.now();
                        if (
                            lastCameraScanRef.current.text === cleanText &&
                            now - lastCameraScanRef.current.timestamp < 2500
                        ) {
                            return;
                        }

                        lastCameraScanRef.current = { text: cleanText, timestamp: now };
                        setManualInput(cleanText);
                        if (handleUniversalScanRef.current) {
                            handleUniversalScanRef.current(cleanText, 'CAMERA');
                        }
                    },
                    () => {
                        // Frame sin QR
                    }
                );

                setCameraActive(true);
                setCameraFacingMode(desiredFacingMode);

                try {
                    const capabilities = qrInstance.getRunningTrackCameraCapabilities();
                    const hasTorch = Boolean(
                        capabilities &&
                            typeof capabilities.torchFeature === 'function' &&
                            capabilities.torchFeature().isSupported()
                    );
                    setTorchSupported(hasTorch);
                } catch {
                    setTorchSupported(false);
                }
            } catch (err) {
                console.warn('Error al iniciar cámara en Scanner PWA:', err);
                setCameraActive(false);
                setCameraError(
                    'No se pudo acceder a la cámara del dispositivo. Verifica los permisos del navegador o utiliza el Lector USB/BT o la Captura Manual.'
                );
            }
        },
        [cameraFacingMode, stopCameraScanner]
    );

    const handleToggleTorch = async () => {
        const instance = html5QrCodeRef.current;
        if (!instance || !cameraActive) return;

        try {
            const nextState = !torchOn;
            const capabilities = instance.getRunningTrackCameraCapabilities();
            if (capabilities && typeof capabilities.torchFeature === 'function') {
                await capabilities.torchFeature().apply(nextState);
                setTorchOn(nextState);
            }
        } catch {
            setCameraError('Tu dispositivo no permitió conmutar el flash/linterna.');
        }
    };

    const handleSwitchCameraFacing = async () => {
        const nextMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
        if (cameraActive) {
            await startCameraScanner(nextMode);
        } else {
            setCameraFacingMode(nextMode);
        }
    };

    // --- Cambio de Modo Operativo Superior (4 Pestañas) ---
    const handleSwitchOperationalMode = (newMode, preferredStationCode = null) => {
        getSharedAudioContext();
        registerOperatorActivity();
        setOperationalMode(newMode);
        setScanResult(null);
        setConcurrentTestResult(null);

        if (newMode === 'ACCESS') {
            const st = preferredStationCode || 'P1_ACCESO';
            setSelectedStationCode(st);
        } else if (newMode === 'POS_STORE') {
            const st = preferredStationCode || 'STAND_XOLOSHOP';
            setSelectedStationCode(st);
            setPosCart({});
            loadPosCatalogForStation(st);
        } else if (newMode === 'ACTIVATION') {
            const st = preferredStationCode || 'ACT_CALIENTE';
            setSelectedStationCode(st);
            const presets = SPONSOR_ACTIVATION_PRESETS[st] || SPONSOR_ACTIVATION_PRESETS.ACT_CALIENTE;
            if (presets[0]) {
                setSelectedPresetId(presets[0].id);
                setActivationTitleInput(presets[0].activationName);
                setActivationResultInput(presets[0].resultSummary);
                setActivationScoreInput(String(presets[0].scoreValue));
            }
        } else if (newMode === 'REWARD_REDEEM') {
            setSelectedStationCode('REWARD_BOOTH');
        }
    };

    // --- Selección de Estación de Trabajo en 1 Toque ---
    const handleSelectStation = (stationCode, stationType = null) => {
        getSharedAudioContext();
        registerOperatorActivity();
        setSelectedStationCode(stationCode);

        if (stationType === 'POS_STORE' || stationCode.startsWith('STAND_')) {
            setPosCart({});
            loadPosCatalogForStation(stationCode);
        } else if (stationType === 'ACTIVATION' || stationCode.startsWith('ACT_')) {
            const presets =
                SPONSOR_ACTIVATION_PRESETS[stationCode] || SPONSOR_ACTIVATION_PRESETS.ACT_CALIENTE;
            if (presets[0]) {
                setSelectedPresetId(presets[0].id);
                setActivationTitleInput(presets[0].activationName);
                setActivationResultInput(presets[0].resultSummary);
                setActivationScoreInput(String(presets[0].scoreValue));
            }
        }

        if (operatorSession) {
            const updated = SaveXfOperatorSession({
                ...operatorSession,
                selectedStationCode: stationCode
            });
            setOperatorSession(updated);
        }
    };

    // --- Controles del Carrito POS Táctil ---
    const handleAdjustCartProduct = (product, delta) => {
        getSharedAudioContext();
        registerOperatorActivity();
        const key = String(product.productId || product.productName);

        setPosCart((prev) => {
            const currentQty = prev[key]?.quantity || 0;
            const nextQty = Math.max(0, currentQty + delta);
            if (nextQty === 0) {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            }
            return {
                ...prev,
                [key]: {
                    product,
                    quantity: nextQty
                }
            };
        });
    };

    const handleClearPosCart = () => {
        setPosCart({});
        setCustomAmount('');
        setCustomConcept('');
    };

    // --- Login Rápido Demo (1 Clic) y Login Operador Real ---
    const handleQuickDemoAuth = async (usernameToUse = selectedDemoUser) => {
        getSharedAudioContext();
        setAuthLoading(true);
        setAuthError('');
        setInactivityLockMessage('');

        const profile =
            DEMO_OPERATOR_PROFILES.find((p) => p.username === usernameToUse) ||
            DEMO_OPERATOR_PROFILES[0];

        const res = await AuthenticateXfDemoOperator(profile.username, profile.role);
        setAuthLoading(false);

        if (!res.success || !res.token) {
            setAuthError(res.message || 'No fue posible autenticar al operador demo.');
            playAccessDeniedTone();
            return;
        }

        const stationToSelect = profile.defaultStation || 'P1_ACCESO';
        const modeToSelect = profile.defaultMode || 'ACCESS';

        const saved = SaveXfOperatorSession({
            token: res.token,
            username: res.username || profile.username,
            role: res.role || 'Operador',
            expiresInMinutes: res.expiresInMinutes || 60,
            selectedStationCode: stationToSelect
        });

        setOperationalMode(modeToSelect);
        setSelectedStationCode(stationToSelect);
        setOperatorSession(saved);
        setSecondsUntilLock(INACTIVITY_TIMEOUT_SECONDS);
        playAccessGrantedTone();
    };

    const handleRealOperatorLogin = async (e) => {
        e.preventDefault();
        getSharedAudioContext();

        if (!realUsername.trim() || !realPassword) {
            setAuthError('Ingresa tu usuario y contraseña de operador.');
            return;
        }

        setAuthLoading(true);
        setAuthError('');
        setInactivityLockMessage('');

        const res = await LoginXfRealOperator(realUsername, realPassword);
        setAuthLoading(false);

        if (!res.success || !res.token) {
            setAuthError(res.message || 'Credenciales de operador incorrectas.');
            playAccessDeniedTone();
            return;
        }

        const saved = SaveXfOperatorSession({
            token: res.token,
            username: res.username || realUsername.trim(),
            role: res.role || 'Operador',
            expiresInMinutes: res.expiresInMinutes || 120,
            selectedStationCode
        });

        setOperatorSession(saved);
        setSecondsUntilLock(INACTIVITY_TIMEOUT_SECONDS);
        setRealPassword('');
        playAccessGrantedTone();
    };

    // --- Switch Rápido RequirePreRegistrationForAccess ---
    const handleTogglePreRegistrationRule = async () => {
        getSharedAudioContext();
        registerOperatorActivity();
        setTogglingPreReg(true);

        const currentVal = Boolean(config?.requirePreRegistrationForAccess);
        const nextVal = !currentVal;

        const res = await UpdateXfAdminConfig({
            requirePreRegistrationForAccess: nextVal
        });
        setTogglingPreReg(false);

        if (res.success && res.data) {
            setConfig(res.data);
        } else {
            setConfig((prev) => ({
                ...(prev || {}),
                requirePreRegistrationForAccess: nextVal
            }));
        }
    };

    // ========================================================================
    // ACCIONES DE SIMULADORES DEMO EN 1 CLIC (HU-04 Y HU-05)
    // ========================================================================

    // --- HU-04: Simulador de Puertas y Protección Civil ---
    const handleDemoScenario1ValidGeneral = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('ACCESS', 'P1_ACCESO');
        setManualInput('XOLO-0001');
        await ResetXfParticipantDemo('XOLO-0001');
        await executeAccessScan('XOLO-0001', 'DEMO_SIMULATOR', 'P1_ACCESO');
    };

    const handleDemoScenario2AntiPassback = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('ACCESS', 'P1_ACCESO');
        setManualInput('XOLO-0001');
        const firstTry = await executeAccessScan('XOLO-0001', 'DEMO_SIMULATOR', 'P1_ACCESO');
        if (firstTry && firstTry.status === 'GRANTED') {
            await executeAccessScan('XOLO-0001', 'DEMO_SIMULATOR', 'P1_ACCESO');
        }
    };

    const handleDemoScenario3RestrictedVip = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('ACCESS', 'VIP_DOOR');
        setManualInput('XOLO-0001');
        await executeAccessScan('XOLO-0001', 'DEMO_SIMULATOR', 'VIP_DOOR');
    };

    const handleDemoScenario4ValidVip = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('ACCESS', 'VIP_DOOR');
        setManualInput('XOLO-0071');
        await executeAccessScan('XOLO-0071', 'DEMO_SIMULATOR', 'VIP_DOOR');
    };

    const handleDemoScenario5ConcurrentAntiPassback = async () => {
        const activeSession = operatorSessionRef.current;
        if (!activeSession?.token) return;

        getSharedAudioContext();
        registerOperatorActivity();
        handleSwitchOperationalMode('ACCESS', 'P1_ACCESO');
        setManualInput('XOLO-0002');
        setScanningInProgress(true);
        setScanResult(null);

        await ResetXfParticipantDemo('XOLO-0002');

        const reqScannerA = CheckXfAccessScan(
            {
                ticketFolioOrToken: 'XOLO-0002',
                stationCode: 'P1_ACCESO',
                scanMethod: 'DEMO_SIMULATOR_GATE_A'
            },
            activeSession.token
        );

        const reqScannerB = new Promise((resolve) => {
            setTimeout(() => {
                resolve(
                    CheckXfAccessScan(
                        {
                            ticketFolioOrToken: 'XOLO-0002',
                            stationCode: 'P1_ACCESO',
                            scanMethod: 'DEMO_SIMULATOR_GATE_B'
                        },
                        activeSession.token
                    )
                );
            }, 50);
        });

        const [resA, resB] = await Promise.all([reqScannerA, reqScannerB]);
        setScanningInProgress(false);

        if (!audioMutedRef.current) {
            playAntiPassbackWarningTone();
        }

        setConcurrentTestResult({
            folio: 'XOLO-0002',
            stationCode: 'P1_ACCESO',
            gateA: resA,
            gateB: resB,
            testedAt: new Date().toLocaleTimeString('es-MX', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            })
        });

        const deniedOne = resB?.status === 'DENIED_ANTI_PASSBACK' ? resB : resA;
        if (deniedOne) {
            setScanResult({
                ...deniedOne,
                operationType: 'ACCESS',
                scannedInput: 'XOLO-0002 (Simultáneo 50ms)',
                scanMethod: 'CONCURRENT_TEST',
                evaluatedAtLocal: new Date().toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            });
        }

        refreshLogsAndZones(activeSession.token);
    };

    const handleDemoResetTicket = async (folioToReset = null) => {
        getSharedAudioContext();
        registerOperatorActivity();
        const targetFolio = (folioToReset || manualInput || 'XOLO-0001').trim().toUpperCase();
        setScanningInProgress(true);
        await ResetXfParticipantDemo(targetFolio);
        setScanningInProgress(false);
        setScanResult(null);
        setConcurrentTestResult(null);
        setIdentifiedParticipant(null);
        await refreshLogsAndZones();
    };

    // --- HU-05: Simulador Demo POS, Activaciones & Canje ---
    const handleHu05Demo1XoloShopSale = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('POS_STORE', 'STAND_XOLOSHOP');
        setManualInput('XOLO-0001');
        const demoItems = [
            {
                productId: 102,
                productName: 'Gorra Edición Xolo Fest',
                quantity: 1,
                unitPrice: 450
            },
            {
                productId: 103,
                productName: 'Bufanda Rojinegra',
                quantity: 1,
                unitPrice: 280
            }
        ];
        await executePosPurchase(
            'XOLO-0001',
            'DEMO_SIMULATOR',
            'STAND_XOLOSHOP',
            demoItems,
            null,
            null
        );
    };

    const handleHu05Demo2TacosSale = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('POS_STORE', 'STAND_TACOS');
        setManualInput('XOLO-0001');
        const demoItems = [
            {
                productId: 201,
                productName: 'Orden 3 Tacos Carne Asada',
                quantity: 1,
                unitPrice: 160
            },
            {
                productId: 203,
                productName: 'Cerveza Artesanal Doble',
                quantity: 1,
                unitPrice: 110
            }
        ];
        await executePosPurchase(
            'XOLO-0001',
            'DEMO_SIMULATOR',
            'STAND_TACOS',
            demoItems,
            null,
            null
        );
    };

    const handleHu05Demo3CalienteActivation = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('ACTIVATION', 'ACT_CALIENTE');
        setManualInput('XOLO-0001');
        setSelectedPresetId('cal_3goles');
        setActivationTitleInput('Tiro a Gol Caliente.mx');
        setActivationResultInput('3 Goles Anotados - Premio Gorra');
        setActivationScoreInput('100');
        await executeSponsorActivation(
            'XOLO-0001',
            'DEMO_SIMULATOR',
            'ACT_CALIENTE',
            'Tiro a Gol Caliente.mx',
            '3 Goles Anotados - Premio Gorra',
            100
        );
    };

    const handleHu05Demo4NissanActivation = async () => {
        getSharedAudioContext();
        handleSwitchOperationalMode('ACTIVATION', 'ACT_NISSAN');
        setManualInput('XOLO-0001');
        setSelectedPresetId('nis_testdrive');
        setActivationTitleInput('Reto Precisión Nissan');
        setActivationResultInput('Test Drive Virtual Completado');
        setActivationScoreInput('100');
        await executeSponsorActivation(
            'XOLO-0001',
            'DEMO_SIMULATOR',
            'ACT_NISSAN',
            'Reto Precisión Nissan',
            'Test Drive Virtual Completado',
            100
        );
    };

    const handleHu05Demo5RedeemGoldenCoupon = async () => {
        const activeSession = operatorSessionRef.current;
        if (!activeSession?.token) return;

        getSharedAudioContext();
        handleSwitchOperationalMode('REWARD_REDEEM', 'REWARD_BOOTH');
        setManualInput('XOLO-0001');
        setScanningInProgress(true);

        // Aseguramos stock físico disponible y que XOLO-0001 tenga su Cupón Dorado listo y sin haber sido canjeado aún
        await SimulateXfStock('AVAILABLE');
        let lookup = await LookupXfParticipantForScanner('XOLO-0001', activeSession.token);

        if (!lookup?.hasEarnedReward || lookup?.hasRedeemedReward || lookup?.receivedDigitalBadge) {
            await ResetXfParticipantDemo('XOLO-0001');
            await CheckXfAccessScan(
                {
                    ticketFolioOrToken: 'XOLO-0001',
                    stationCode: 'P1_ACCESO',
                    scanMethod: 'DEMO_SIMULATOR'
                },
                activeSession.token
            );
            await RecordXfPosPurchase(
                {
                    ticketFolioOrToken: 'XOLO-0001',
                    stationCode: 'STAND_XOLOSHOP',
                    items: [
                        {
                            productName: 'Gorra Edición Xolo Fest',
                            quantity: 1,
                            unitPrice: 450
                        }
                    ],
                    scanMethod: 'DEMO_SIMULATOR'
                },
                activeSession.token
            );
            await RecordXfPosPurchase(
                {
                    ticketFolioOrToken: 'XOLO-0001',
                    stationCode: 'STAND_TACOS',
                    items: [
                        {
                            productName: 'Orden 3 Tacos Carne Asada',
                            quantity: 1,
                            unitPrice: 160
                        }
                    ],
                    scanMethod: 'DEMO_SIMULATOR'
                },
                activeSession.token
            );
            await RecordXfSponsorActivation(
                {
                    ticketFolioOrToken: 'XOLO-0001',
                    stationCode: 'ACT_CALIENTE',
                    activationName: 'Tiro a Gol Caliente.mx',
                    resultSummary: '3 Goles Anotados - Premio Gorra',
                    scoreValue: 100,
                    scanMethod: 'DEMO_SIMULATOR'
                },
                activeSession.token
            );
            lookup = await LookupXfParticipantForScanner('XOLO-0001', activeSession.token);
        }

        const codeToRedeem = lookup?.rewardCouponCode || 'XOLO-0001';
        setManualInput(codeToRedeem);
        await executeRewardRedemption(codeToRedeem, 'DEMO_SIMULATOR');
    };

    const handleHu05Demo6DoubleRedeemWarning = async () => {
        const activeSession = operatorSessionRef.current;
        if (!activeSession?.token) return;

        getSharedAudioContext();
        handleSwitchOperationalMode('REWARD_REDEEM', 'REWARD_BOOTH');

        // Consultamos si XOLO-0001 ya fue canjeado; si no, lo canjeamos primero para que el segundo intento dispare ALREADY_REDEEMED
        const lookup = await LookupXfParticipantForScanner('XOLO-0001', activeSession.token);
        const codeToUse = lookup?.rewardCouponCode || 'XOLO-0001';
        setManualInput(codeToUse);

        const firstAttempt = await executeRewardRedemption(codeToUse, 'DEMO_SIMULATOR');
        if (firstAttempt && firstAttempt.status === 'REWARD_REDEEMED') {
            await executeRewardRedemption(codeToUse, 'DEMO_SIMULATOR');
        }
    };

    // --- Filtrado de estaciones según pestaña activa ---
    const posStations = allStations.filter((s) => s.stationType === 'POS_STORE');
    const activationStations = allStations.filter((s) => s.stationType === 'ACTIVATION');

    const currentStation =
        allStations.find((s) => s.stationCode === selectedStationCode) ||
        FALLBACK_STATIONS_CATALOG.find((s) => s.stationCode === selectedStationCode) ||
        FALLBACK_STATIONS_CATALOG[0];

    const currentZone =
        zones.find((z) => z.zoneCode === currentStation?.zoneCode) || {
            zoneCode: currentStation?.zoneCode || 'GEN',
            name: currentStation?.zoneName || 'Explanada General',
            allowReentry: Boolean(currentStation?.allowReentry),
            allowedCategories: currentStation?.allowedCategories || ['GENERAL', 'VIP', 'CANCHA'],
            capacityLimit: currentStation?.capacityLimit || 5000,
            currentOccupancy: currentStation?.currentOccupancy || 0,
            semaphoreStatus: 'GREEN'
        };

    const semaphore = getSemaphoreBadge(
        currentZone.currentOccupancy,
        currentZone.capacityLimit,
        currentZone.semaphoreStatus
    );

    const currentActivationPresets =
        SPONSOR_ACTIVATION_PRESETS[selectedStationCode] || SPONSOR_ACTIVATION_PRESETS.ACT_CALIENTE;

    // ========================================================================
    // VISTA 1: PANTALLA DE LOGIN TÁCTIL DE OPERADOR / BLOQUEO POR INACTIVIDAD
    // ========================================================================
    if (!operatorSession?.token) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    background: 'radial-gradient(circle at top, #1f0909 0%, #09090b 70%)',
                    color: '#ffffff',
                    fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
                    padding: '20px 16px 48px'
                }}
            >
                <div style={{ maxWidth: 540, margin: '0 auto' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 18
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span
                                style={{
                                    background: '#dc2626',
                                    color: '#fff',
                                    fontWeight: 900,
                                    fontSize: 12,
                                    padding: '5px 10px',
                                    borderRadius: 999,
                                    letterSpacing: 0.8
                                }}
                            >
                                PWA STAFF • HU-04 & HU-05
                            </span>
                            <span style={{ color: '#a1a1aa', fontSize: 12, fontWeight: 700 }}>
                                ACCESOS • POS • ACTIVACIONES • CANJE
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate('/demo/xolofest')}
                            style={{
                                background: 'rgba(255,255,255,0.08)',
                                border: '1px solid rgba(255,255,255,0.18)',
                                color: '#e4e4e7',
                                borderRadius: 10,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer'
                            }}
                        >
                            ← Ir a Landing Fan
                        </button>
                    </div>

                    {inactivityLockMessage && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                background: 'linear-gradient(135deg, #92400e 0%, #78350f 100%)',
                                border: '2px solid #f59e0b',
                                borderRadius: 16,
                                padding: '16px 18px',
                                marginBottom: 18,
                                boxShadow: '0 10px 30px rgba(245, 158, 11, 0.25)'
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 900,
                                    fontSize: 15,
                                    color: '#fde68a',
                                    marginBottom: 4
                                }}
                            >
                                🔒 TERMINAL BLOQUEADA POR SEGURIDAD
                            </div>
                            <div style={{ fontSize: 13.5, color: '#fef3c7', lineHeight: 1.45 }}>
                                {inactivityLockMessage}
                            </div>
                        </motion.div>
                    )}

                    <div
                        style={{
                            background: 'rgba(20, 20, 25, 0.96)',
                            border: '2px solid rgba(212, 175, 55, 0.45)',
                            borderRadius: 22,
                            padding: '24px 20px',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.65)'
                        }}
                    >
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 64,
                                    height: 64,
                                    borderRadius: 18,
                                    background: 'rgba(220, 38, 38, 0.18)',
                                    border: '2px solid #dc2626',
                                    fontSize: 30,
                                    marginBottom: 12
                                }}
                            >
                                🛡️
                            </div>
                            <h1
                                style={{
                                    fontSize: 22,
                                    fontWeight: 900,
                                    margin: '0 0 6px',
                                    letterSpacing: -0.3
                                }}
                            >
                                SCANNER WEB PWA MULTI-ESTACIÓN
                            </h1>
                            <p style={{ color: '#a1a1aa', fontSize: 13.5, margin: 0 }}>
                                Puertas de Acceso, Punto de Venta POS Táctil, Activaciones de
                                Patrocinador y Canje de Souvenirs (#CANJE).
                            </p>
                        </div>

                        <div
                            style={{
                                background: 'rgba(16, 185, 129, 0.1)',
                                border: '2px solid #10b981',
                                borderRadius: 16,
                                padding: 16,
                                marginBottom: 20
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    color: '#6ee7b7',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.8,
                                    marginBottom: 8
                                }}
                            >
                                🚀 Modo Demostración Instantáneo (Sin Contraseña)
                            </div>

                            <button
                                type="button"
                                disabled={authLoading}
                                onClick={() => handleQuickDemoAuth('staff_puerta1')}
                                style={{
                                    width: '100%',
                                    padding: '15px 16px',
                                    borderRadius: 14,
                                    border: 'none',
                                    background:
                                        'linear-gradient(135deg, #059669 0%, #10b981 50%, #047857 100%)',
                                    color: '#ffffff',
                                    fontWeight: 900,
                                    fontSize: 15.5,
                                    cursor: authLoading ? 'wait' : 'pointer',
                                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.35)',
                                    marginBottom: 12
                                }}
                            >
                                {authLoading
                                    ? '⏳ Autenticando terminal...'
                                    : '⚡ Acceso Rápido Operador Demo (1 Clic - staff_puerta1)'}
                            </button>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'center',
                                    flexWrap: 'wrap'
                                }}
                            >
                                <select
                                    value={selectedDemoUser}
                                    onChange={(e) => setSelectedDemoUser(e.target.value)}
                                    style={{
                                        flex: 1,
                                        minWidth: 200,
                                        background: '#09090b',
                                        color: '#ffffff',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        borderRadius: 10,
                                        padding: '10px 12px',
                                        fontSize: 13,
                                        fontWeight: 700
                                    }}
                                >
                                    {DEMO_OPERATOR_PROFILES.map((op) => (
                                        <option key={op.username} value={op.username}>
                                            {op.label}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    disabled={authLoading}
                                    onClick={() => handleQuickDemoAuth(selectedDemoUser)}
                                    style={{
                                        background: '#f59e0b',
                                        color: '#09090b',
                                        border: 'none',
                                        borderRadius: 10,
                                        padding: '10px 14px',
                                        fontWeight: 900,
                                        fontSize: 13,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Entrar con Perfil →
                                </button>
                            </div>
                        </div>

                        {authError && (
                            <div
                                style={{
                                    background: 'rgba(220, 38, 38, 0.18)',
                                    border: '1px solid #ef4444',
                                    color: '#fecaca',
                                    padding: '10px 14px',
                                    borderRadius: 12,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    marginBottom: 16
                                }}
                            >
                                ⛔ {authError}
                            </div>
                        )}

                        <form onSubmit={handleRealOperatorLogin}>
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                    color: '#a1a1aa',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.7,
                                    marginBottom: 10
                                }}
                            >
                                🔐 Acceso con Cuenta de Operador Interaktia V2 (/api/v2/auth/login)
                            </div>

                            <div style={{ marginBottom: 12 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#d4d4d8',
                                        marginBottom: 4
                                    }}
                                >
                                    Usuario de Operador
                                </label>
                                <input
                                    type="text"
                                    value={realUsername}
                                    onChange={(e) => setRealUsername(e.target.value)}
                                    placeholder="Ej. operador_puerta1"
                                    style={{
                                        width: '100%',
                                        background: '#09090b',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        color: '#ffffff',
                                        fontSize: 14,
                                        fontWeight: 600
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: 16 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#d4d4d8',
                                        marginBottom: 4
                                    }}
                                >
                                    Contraseña
                                </label>
                                <input
                                    type="password"
                                    value={realPassword}
                                    onChange={(e) => setRealPassword(e.target.value)}
                                    placeholder="••••••••"
                                    style={{
                                        width: '100%',
                                        background: '#09090b',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 12,
                                        padding: '12px 14px',
                                        color: '#ffffff',
                                        fontSize: 14,
                                        fontWeight: 600
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={authLoading}
                                style={{
                                    width: '100%',
                                    padding: '13px 16px',
                                    borderRadius: 12,
                                    border: '1px solid rgba(220, 38, 38, 0.7)',
                                    background: 'rgba(220, 38, 38, 0.2)',
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: 14,
                                    cursor: 'pointer'
                                }}
                            >
                                Iniciar Sesión con Credenciales Reales
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ========================================================================
    // VISTA 2: TERMINAL OPERATIVA MULTI-ESTACIÓN (HU-04 + HU-05)
    // ========================================================================
    const requirePreReg = Boolean(config?.requirePreRegistrationForAccess);
    const minsLeft = Math.floor(secondsUntilLock / 60);
    const secsLeft = secondsUntilLock % 60;
    const currentModeObj =
        OPERATIONAL_MODES.find((m) => m.mode === operationalMode) || OPERATIONAL_MODES[0];

    return (
        <div
            style={{
                minHeight: '100vh',
                background: "url('/img/page/backgrounds/Fondo-SelfieFan-Femenil.jpeg') center top / cover no-repeat fixed",
                color: '#ffffff',
                fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
                paddingBottom: 60
            }}
        >
            {/* 1. CABECERA TÁCTIL DE OPERADOR */}
            <header
                style={{
                    background: 'rgba(24, 24, 27, 0.96)',
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    padding: '8px 14px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 40
                }}
            >
                <div
                    style={{
                        maxWidth: 1180,
                        margin: '0 auto',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 10
                    }}
                >
                    {/* Lado Izquierdo: Marca, Operador e Indicador de Conexión */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span
                            style={{
                                background: '#dc2626',
                                color: '#ffffff',
                                fontWeight: 900,
                                fontSize: 11.5,
                                padding: '4px 8px',
                                borderRadius: 6,
                                letterSpacing: 0.5
                            }}
                        >
                            XOLO SCANNER
                        </span>

                        <div
                            style={{
                                fontSize: 12,
                                color: '#d4d4d8',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                            }}
                        >
                            <span style={{ fontWeight: 700 }}>👤 {operatorSession.username}</span>
                        </div>

                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '3px 8px',
                                borderRadius: 999,
                                background: signalrConnected
                                    ? 'rgba(16, 185, 129, 0.14)'
                                    : 'rgba(245, 158, 11, 0.14)',
                                color: signalrConnected ? '#34d399' : '#fbbf24',
                                border: `1px solid ${
                                    signalrConnected
                                        ? 'rgba(16, 185, 129, 0.4)'
                                        : 'rgba(245, 158, 11, 0.4)'
                                }`
                            }}
                        >
                            <span
                                style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: signalrConnected ? '#10b981' : '#f59e0b'
                                }}
                            />
                            {signalrConnected ? 'En línea' : 'Sin red'}
                        </span>
                    </div>

                    {/* Lado Derecho: Menú de Función + Toggle Menú */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Selector de Función (Dropdown menú) */}
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => setShowModeMenu((prev) => !prev)}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: `1.5px solid ${currentModeObj.accent}`,
                                    color: '#ffffff',
                                    borderRadius: 10,
                                    padding: '6px 12px',
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    cursor: 'pointer'
                                }}
                                title="Cambiar función: Puertas, POS, Activación o Canje"
                            >
                                <span>{currentModeObj.label}</span>
                                <i className="bi bi-chevron-down" style={{ fontSize: 10 }}></i>
                            </button>

                            <AnimatePresence>
                                {showModeMenu && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95, y: -5 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, y: -5 }}
                                        transition={{ duration: 0.15 }}
                                        style={{
                                            position: 'absolute',
                                            top: '115%',
                                            right: 0,
                                            width: 250,
                                            background: '#18181b',
                                            border: '1px solid rgba(255, 255, 255, 0.16)',
                                            borderRadius: 14,
                                            boxShadow: '0 15px 35px rgba(0,0,0,0.85)',
                                            zIndex: 100,
                                            padding: 8
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 10.5,
                                                fontWeight: 800,
                                                color: '#a1a1aa',
                                                padding: '6px 10px',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.6
                                            }}
                                        >
                                            Seleccionar Función
                                        </div>
                                        {OPERATIONAL_MODES.map((tab) => {
                                            const isActive = operationalMode === tab.mode;
                                            return (
                                                <button
                                                    key={tab.mode}
                                                    type="button"
                                                    onClick={() => {
                                                        handleSwitchOperationalMode(tab.mode);
                                                        setShowModeMenu(false);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '10px 12px',
                                                        borderRadius: 10,
                                                        border: 'none',
                                                        background: isActive
                                                            ? 'rgba(255, 255, 255, 0.1)'
                                                            : 'transparent',
                                                        color: isActive ? '#ffffff' : '#d4d4d8',
                                                        cursor: 'pointer',
                                                        fontSize: 13,
                                                        fontWeight: isActive ? 800 : 600,
                                                        marginBottom: 3
                                                    }}
                                                >
                                                    <span>{tab.label}</span>
                                                    {isActive && (
                                                        <i
                                                            className="bi bi-check-lg"
                                                            style={{
                                                                color: tab.accent,
                                                                fontWeight: 900
                                                            }}
                                                        ></i>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Botón para Mostrar / Ocultar el Menú / Opciones Superiores */}
                        <button
                            type="button"
                            onClick={() => setShowTopHeader((prev) => !prev)}
                            style={{
                                background: showTopHeader
                                    ? 'rgba(255, 255, 255, 0.14)'
                                    : 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                color: '#ffffff',
                                borderRadius: 10,
                                padding: '6px 10px',
                                fontSize: 12,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                                cursor: 'pointer'
                            }}
                            title={
                                showTopHeader
                                    ? 'Ocultar controles administrativos'
                                    : 'Mostrar controles administrativos'
                            }
                        >
                            <i className={showTopHeader ? 'bi bi-chevron-up' : 'bi bi-sliders'}></i>
                            <span className="d-none d-sm-inline">
                                {showTopHeader ? 'Ocultar' : 'Menú'}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Subpanel Colapsable de Controles Administrativos */}
                <AnimatePresence>
                    {showTopHeader && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{
                                overflow: 'hidden',
                                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                                marginTop: 8,
                                paddingTop: 8
                            }}
                        >
                            <div
                                style={{
                                    maxWidth: 1180,
                                    margin: '0 auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setDemoDrawerOpen(true)}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.14)',
                                            color: '#ffffff',
                                            borderRadius: 8,
                                            padding: '5px 10px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 5
                                        }}
                                        title="Abrir Centro de Pruebas y Simuladores Demo"
                                    >
                                        <span>🛠️ Simuladores</span>
                                    </button>

                                    <button
                                        type="button"
                                        disabled={togglingPreReg}
                                        onClick={handleTogglePreRegistrationRule}
                                        style={{
                                            background: requirePreReg
                                                ? 'rgba(220, 38, 38, 0.2)'
                                                : 'rgba(255, 255, 255, 0.08)',
                                            border: `1px solid ${
                                                requirePreReg
                                                    ? '#ef4444'
                                                    : 'rgba(255, 255, 255, 0.14)'
                                            }`,
                                            color: requirePreReg ? '#fca5a5' : '#e4e4e7',
                                            borderRadius: 8,
                                            padding: '5px 10px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {requirePreReg
                                            ? '🛑 Exigir Pre-Registro'
                                            : '🚶 Acceso Ágil Libre'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const nextMute = !audioMuted;
                                            setAudioMuted(nextMute);
                                            if (!nextMute) {
                                                playAccessGrantedTone();
                                            }
                                        }}
                                        style={{
                                            background: audioMuted
                                                ? 'rgba(239, 68, 68, 0.2)'
                                                : 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.14)',
                                            color: '#ffffff',
                                            borderRadius: 8,
                                            padding: '5px 10px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {audioMuted ? '🔇 Audio Silenciado' : '🔊 Audio Activo'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            triggerInactivityLock(
                                                '🔒 Bloqueo por Inactividad activado. Requiere reautenticación.'
                                            )
                                        }
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.14)',
                                            color: '#d4d4d8',
                                            borderRadius: 8,
                                            padding: '5px 10px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        🔒 Bloquear
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigate('/demo/xolofest/passport')}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.14)',
                                            color: '#ffffff',
                                            borderRadius: 8,
                                            padding: '5px 10px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        📱 Pasaporte
                                    </button>
                                </div>

                                <div
                                    style={{
                                        color: '#71717a',
                                        fontSize: 11.5,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    Sesión: {minsLeft}:{String(secsLeft).padStart(2, '0')}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            <main style={{ maxWidth: 1180, margin: '0 auto', padding: '16px 14px' }}>
                {/* BANNER DE RESILIENCIA PERIMETRAL OFFLINE Y NOTIFICACIÓN DE SINCRONIZACIÓN (HU-07) */}
                <AnimatePresence>
                    {(!isOnline || isSimulatedOffline || offlineQueue.length > 0) && (
                        <motion.div
                            key="offline-banner"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                background: 'linear-gradient(135deg, #92400e 0%, #78350f 100%)',
                                border: '2px solid #f59e0b',
                                borderRadius: 16,
                                padding: '14px 18px',
                                marginBottom: 16,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)'
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontWeight: 900,
                                        fontSize: 14.5,
                                        color: '#fde68a',
                                        letterSpacing: 0.3
                                    }}
                                >
                                    📶 MODO OFFLINE ACTIVO — {offlineQueue.length} escaneos en cola
                                    local esperando reconexión
                                </div>
                                <div style={{ fontSize: 12.5, color: '#fef3c7', marginTop: 3 }}>
                                    {isSimulatedOffline
                                        ? 'Simulación de corte de red perimetral activa. Los escaneos en puerta se guardan en localStorage con clientScanId único.'
                                        : 'Los escaneos pendientes se sincronizarán de forma idempotente en cuanto regrese la red.'}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {offlineQueue.length > 0 && (
                                    <button
                                        type="button"
                                        disabled={syncingOfflineQueue}
                                        onClick={ async () => {
                                            if (isSimulatedOffline) {
                                                isSimulatedOfflineRef.current = false;
                                                setIsSimulatedOffline(false);
                                            }
                                            await handleSyncOfflineQueue();
                                        }}
                                        style={{
                                            background: '#10b981',
                                            color: '#09090b',
                                            border: 'none',
                                            borderRadius: 10,
                                            padding: '8px 14px',
                                            fontWeight: 900,
                                            fontSize: 12.5,
                                            cursor: syncingOfflineQueue ? 'wait' : 'pointer'
                                        }}
                                    >
                                        {syncingOfflineQueue
                                            ? '⏳ Sincronizando cola...'
                                            : `🔄 Sincronizar Ahora (${offlineQueue.length})`}
                                    </button>
                                )}

                                {isSimulatedOffline && (
                                    <button
                                        type="button"
                                        onClick={handleToggleSimulateOffline}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.14)',
                                            color: '#ffffff',
                                            border: '1px solid #fde68a',
                                            borderRadius: 10,
                                            padding: '8px 13px',
                                            fontWeight: 800,
                                            fontSize: 12,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        🟢 Restaurar Red Perimetral
                                    </button>
                                )}

                                {offlineQueue.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            ClearOfflineScanQueue();
                                            setOfflineQueue([]);
                                        }}
                                        style={{
                                            background: 'rgba(0, 0, 0, 0.25)',
                                            color: '#fde68a',
                                            border: '1px solid rgba(253, 230, 138, 0.4)',
                                            borderRadius: 10,
                                            padding: '8px 11px',
                                            fontWeight: 700,
                                            fontSize: 11.5,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        🗑️ Vaciar Cola
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {syncSuccessBanner && (
                        <motion.div
                            key="sync-success-banner"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
                                border: '2px solid #10b981',
                                borderRadius: 16,
                                padding: '13px 18px',
                                marginBottom: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                boxShadow: '0 10px 28px rgba(16, 185, 129, 0.3)'
                            }}
                        >
                            <div style={{ fontWeight: 900, fontSize: 14.5, color: '#ecfdf5' }}>
                                {syncSuccessBanner}
                            </div>
                            <button
                                type="button"
                                onClick={() => setSyncSuccessBanner('')}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#a7f3d0',
                                    fontWeight: 900,
                                    fontSize: 13,
                                    cursor: 'pointer'
                                }}
                            >
                                ✕
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* MODAL DE SELECCIÓN DE PUESTO POR TILES (/admin/dashboard style) */}
                <StationTilesModal
                    isOpen={showStationSelector}
                    onClose={() => setShowStationSelector(false)}
                    currentStationCode={selectedStationCode}
                    currentMode={operationalMode}
                    onSelectStation={(stationCode, mode) => {
                        handleSwitchOperationalMode(mode, stationCode);
                        handleSelectStation(stationCode, mode);
                        setShowStationSelector(false);
                    }}
                    stations={allStations}
                    zones={zones}
                />

                {/* CAJÓN LATERAL DE PRUEBAS Y SIMULADORES DEMO (HU-04 / HU-05) */}
                <ScannerDemoDrawer
                    isOpen={demoDrawerOpen}
                    onClose={() => setDemoDrawerOpen(false)}
                    demoBarTab={demoBarTab}
                    setDemoBarTab={setDemoBarTab}
                    scanningInProgress={scanningInProgress}
                    manualInput={manualInput}
                    // HU-05
                    onHu05XoloShopSale={handleHu05Demo1XoloShopSale}
                    onHu05TacosSale={handleHu05Demo2TacosSale}
                    onHu05CalienteActivation={handleHu05Demo3CalienteActivation}
                    onHu05NissanActivation={handleHu05Demo4NissanActivation}
                    onHu05RedeemGoldenCoupon={handleHu05Demo5RedeemGoldenCoupon}
                    onHu05DoubleRedeemWarning={handleHu05Demo6DoubleRedeemWarning}
                    // HU-04
                    onHu04ValidGeneral={handleDemoScenario1ValidGeneral}
                    onHu04AntiPassback={handleDemoScenario2AntiPassback}
                    onHu04RestrictedVip={handleDemoScenario3RestrictedVip}
                    onHu04ValidVip={handleDemoScenario4ValidVip}
                    onHu04ConcurrentAntiPassback={handleDemoScenario5ConcurrentAntiPassback}
                    onDemoResetTicket={(fol) => handleDemoResetTicket(fol)}
                    // Red y Audio
                    isSimulatedOffline={isSimulatedOffline}
                    offlineQueueLength={offlineQueue.length}
                    onToggleSimulateOffline={handleToggleSimulateOffline}
                    onSyncOfflineQueue={async () => {
                        if (isSimulatedOffline) {
                            isSimulatedOfflineRef.current = false;
                            setIsSimulatedOffline(false);
                        }
                        await handleSyncOfflineQueue();
                    }}
                    syncingOfflineQueue={syncingOfflineQueue}
                    onClearOfflineQueue={() => {
                        ClearOfflineScanQueue();
                        setOfflineQueue([]);
                    }}
                    onPlayGrantedTone={playAccessGrantedTone}
                    onPlayWarningTone={playAntiPassbackWarningTone}
                    onPlayDeniedTone={playAccessDeniedTone}
                />

                {/* 2. TARJETA DE PUESTO DE TRABAJO ACTIVO */}
                <div
                    style={{
                        background: 'rgba(24, 24, 27, 0.92)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: 16,
                        padding: '14px 18px',
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                background: 'rgba(255, 255, 255, 0.06)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 20
                            }}
                        >
                            <i
                                className={
                                    operationalMode === 'ACCESS'
                                        ? 'bi bi-door-open-fill'
                                        : operationalMode === 'POS_STORE'
                                        ? 'bi bi-shop'
                                        : operationalMode === 'ACTIVATION'
                                        ? 'bi bi-trophy-fill'
                                        : 'bi bi-gift-fill'
                                }
                            ></i>
                        </div>
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#a1a1aa',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.8
                                }}
                            >
                                Puesto de Trabajo Asignado
                            </div>
                            <div style={{ fontSize: 17, fontWeight: 900, color: '#ffffff' }}>
                                {currentStation?.stationName || selectedStationCode}
                                {currentStation?.zoneName && (
                                    <span
                                        style={{
                                            fontSize: 12.5,
                                            color: '#a1a1aa',
                                            fontWeight: 600,
                                            marginLeft: 8
                                        }}
                                    >
                                        • {currentStation.zoneName}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={() => setShowStationSelector(true)}
                            style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.18)',
                                borderRadius: 10,
                                padding: '8px 14px',
                                fontSize: 12.5,
                                fontWeight: 800,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                            }}
                        >
                            <i className="bi bi-grid-fill"></i>
                            <span>Cambiar Puesto (Tiles)</span>
                        </button>
                    </div>
                </div>

                {/* 4. TARJETA DE AFICIONADO IDENTIFICADO (FLUJO DUAL FLEXIBLE: ORDEN 2) */}
                <AnimatePresence>
                    {identifiedParticipant && (
                        <motion.section
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            style={{
                                background:
                                    'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(24, 24, 27, 0.98) 100%)',
                                border: '2px solid #10b981',
                                borderRadius: 16,
                                padding: '14px 18px',
                                marginBottom: 16,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 12
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 900,
                                        color: '#6ee7b7',
                                        letterSpacing: 0.7,
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    👤 AFICIONADO IDENTIFICADO EN ESTACIÓN (FLUJO DUAL ACTIVO)
                                </div>
                                <div
                                    style={{
                                        fontSize: 18,
                                        fontWeight: 900,
                                        color: '#ffffff',
                                        marginTop: 2
                                    }}
                                >
                                    {identifiedParticipant.participantName || 'Aficionado Xolo'}{' '}
                                    <span style={{ color: '#fde68a', fontFamily: 'monospace' }}>
                                        (#{identifiedParticipant.ticketFolio})
                                    </span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                        fontSize: 12,
                                        color: '#d4d4d8',
                                        marginTop: 4,
                                        fontWeight: 700
                                    }}
                                >
                                    <span>
                                        Categoría: <strong>{identifiedParticipant.ticketCategory}</strong>
                                    </span>
                                    <span>
                                        Gasto Acumulado:{' '}
                                        <strong style={{ color: '#34d399' }}>
                                            {formatCurrencyMxn(identifiedParticipant.totalSpentAmount)}
                                        </strong>{' '}
                                        ({identifiedParticipant.totalPurchasesCount || 0} compras)
                                    </span>
                                    <span>
                                        Activaciones:{' '}
                                        <strong>
                                            {identifiedParticipant.completedActivationsCount || 0}
                                        </strong>
                                    </span>
                                    {identifiedParticipant.rewardCouponCode && (
                                        <span style={{ color: '#fde68a', fontFamily: 'monospace' }}>
                                            🎁 Cupón: #{identifiedParticipant.rewardCouponCode}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setIdentifiedParticipant(null)}
                                style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.25)',
                                    color: '#ffffff',
                                    borderRadius: 10,
                                    padding: '7px 12px',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    cursor: 'pointer'
                                }}
                            >
                                ✕ Cambiar Aficionado
                            </button>
                        </motion.section>
                    )}
                </AnimatePresence>

                {/* 5. RECUADRO COMPARATIVO DE PRUEBA DE CONCURRENCIA (HU-04: 50ms) */}
                <AnimatePresence>
                    {concurrentTestResult && (
                        <motion.section
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            style={{
                                background: '#18181b',
                                border: '2px solid #a78bfa',
                                borderRadius: 18,
                                padding: 16,
                                marginBottom: 18,
                                boxShadow: '0 12px 35px rgba(139, 92, 246, 0.25)'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12
                                }}
                            >
                                <div style={{ fontWeight: 900, fontSize: 15, color: '#ddd6fe' }}>
                                    ⚡ RESULTADO DE CONCURRENCIA ATÓMICA (2 LECTORES CON 50ms DE DIFERENCIA — FOLIO{' '}
                                    {concurrentTestResult.folio})
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setConcurrentTestResult(null)}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        color: '#fff',
                                        borderRadius: 8,
                                        padding: '4px 10px',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer'
                                    }}
                                >
                                    ✕ Cerrar Comparativa
                                </button>
                            </div>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: 12
                                }}
                            >
                                {[
                                    {
                                        label: '📱 Escáner Puerta A (0 ms)',
                                        data: concurrentTestResult.gateA
                                    },
                                    {
                                        label: '📱 Escáner Puerta B (+50 ms)',
                                        data: concurrentTestResult.gateB
                                    }
                                ].map((item, idx) => {
                                    const isGranted = item.data?.status === 'GRANTED';
                                    const isAntiPassback =
                                        item.data?.status === 'DENIED_ANTI_PASSBACK';
                                    const cardBg = isGranted
                                        ? '#065f46'
                                        : isAntiPassback
                                        ? '#92400e'
                                        : '#7f1d1d';
                                    const cardBorder = isGranted
                                        ? '#10b981'
                                        : isAntiPassback
                                        ? '#f59e0b'
                                        : '#ef4444';

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                background: cardBg,
                                                border: `2px solid ${cardBorder}`,
                                                borderRadius: 14,
                                                padding: 14
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    color: '#fde68a',
                                                    marginBottom: 4
                                                }}
                                            >
                                                {item.label}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 17,
                                                    fontWeight: 900,
                                                    marginBottom: 6
                                                }}
                                            >
                                                {isGranted
                                                    ? '✅ ACCESO CONCEDIDO (GRANTED)'
                                                    : isAntiPassback
                                                    ? '⚠️ DOBLE ENTRADA (DENIED_ANTI_PASSBACK)'
                                                    : `⛔ ${item.data?.status || 'DENIED'}`}
                                            </div>
                                            <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                                                {item.data?.message}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>

                {/* 6. SEMAFORIZACIÓN GIGANTE DE ALTO CONTRASTE (RESULTADO DE OPERACIÓN BAJO EL SOL) */}
                <AnimatePresence mode="wait">
                    {scanResult && (
                        <motion.section
                            key={`${scanResult.status}-${scanResult.evaluatedAtLocal}`}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.96 }}
                            transition={{ duration: 0.18 }}
                            style={{
                                background:
                                    scanResult.colorTheme === 'GREEN'
                                        ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                        : scanResult.colorTheme === 'YELLOW'
                                        ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                                        : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                                border:
                                    scanResult.colorTheme === 'GREEN'
                                        ? '4px solid #6ee7b7'
                                        : scanResult.colorTheme === 'YELLOW'
                                        ? '4px solid #fde68a'
                                        : '4px solid #fca5a5',
                                borderRadius: 22,
                                padding: '22px 20px',
                                marginBottom: 20,
                                boxShadow: '0 20px 45px rgba(0,0,0,0.65)'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 12,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div
                                        style={{
                                            fontSize: 48,
                                            lineHeight: 1,
                                            filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.4))'
                                        }}
                                    >
                                        {scanResult.colorTheme === 'GREEN'
                                            ? '✅'
                                            : scanResult.colorTheme === 'YELLOW'
                                            ? '⚠️'
                                            : '⛔'}
                                    </div>
                                    <div>
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                background: 'rgba(0,0,0,0.35)',
                                                padding: '4px 10px',
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 900,
                                                fontFamily: 'monospace',
                                                letterSpacing: 0.8,
                                                marginBottom: 4
                                            }}
                                        >
                                            {scanResult.status} • AUDIO: {scanResult.audioCue} •{' '}
                                            {scanResult.evaluatedAtLocal}
                                        </div>
                                        <h2
                                            style={{
                                                fontSize: 27,
                                                fontWeight: 900,
                                                margin: 0,
                                                letterSpacing: -0.5,
                                                textShadow: '0 2px 8px rgba(0,0,0,0.4)'
                                            }}
                                        >
                                            {scanResult.title ||
                                                (scanResult.colorTheme === 'GREEN'
                                                    ? 'OPERACIÓN EXITOSA'
                                                    : scanResult.colorTheme === 'YELLOW'
                                                    ? 'ALERTA PREVENTIVA'
                                                    : 'OPERACIÓN RECHAZADA')}
                                        </h2>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setScanResult(null)}
                                    style={{
                                        background: 'rgba(0,0,0,0.35)',
                                        border: '2px solid rgba(255,255,255,0.5)',
                                        color: '#ffffff',
                                        borderRadius: 12,
                                        padding: '8px 14px',
                                        fontWeight: 900,
                                        fontSize: 13,
                                        cursor: 'pointer'
                                    }}
                                >
                                    ✕ Limpiar Pantalla
                                </button>
                            </div>

                            <div
                                style={{
                                    marginTop: 14,
                                    background: 'rgba(0,0,0,0.3)',
                                    borderRadius: 14,
                                    padding: '14px 16px',
                                    fontSize: 18,
                                    fontWeight: 800,
                                    lineHeight: 1.4
                                }}
                            >
                                {scanResult.message}
                            </div>

                            {/* Detalle Anti-Passback (HU-04) */}
                            {scanResult.status === 'DENIED_ANTI_PASSBACK' && (
                                <div
                                    style={{
                                        marginTop: 12,
                                        background: 'rgba(0,0,0,0.45)',
                                        border: '2px solid #fde68a',
                                        borderRadius: 12,
                                        padding: '12px 16px',
                                        fontSize: 15.5,
                                        fontWeight: 900,
                                        color: '#fef08a'
                                    }}
                                >
                                    ⚠️ BOLETO YA INGRESÓ A ESTA ZONA EL{' '}
                                    {formatTimeLocal(
                                        scanResult.firstEntryAtUtc || scanResult.timestampUtc
                                    )}{' '}
                                    POR{' '}
                                    {(
                                        scanResult.firstEntryOperator ||
                                        scanResult.operatorUsername ||
                                        'STAFF'
                                    ).toUpperCase()}
                                    {scanResult.firstEntryStationName
                                        ? ` (${scanResult.firstEntryStationName})`
                                        : ''}
                                </div>
                            )}

                            {/* Detalle de Cupón Dorado Desbloqueado en POS o Activación (HU-05) */}
                            {scanResult.rewardEvaluation?.hasEarnedReward &&
                                scanResult.rewardEvaluation?.rewardCouponCode && (
                                    <div
                                        style={{
                                            marginTop: 12,
                                            background: 'rgba(0,0,0,0.48)',
                                            border: '2px solid #fde68a',
                                            borderRadius: 14,
                                            padding: '12px 16px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 10
                                        }}
                                    >
                                        <div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    color: '#fde68a'
                                                }}
                                            >
                                                🏆 ¡RECOMPENSA DEL PASAPORTE DIGITAL DESBLOQUEADA!
                                            </div>
                                            <div style={{ fontSize: 16, fontWeight: 900 }}>
                                                Cupón Dorado Oficial:{' '}
                                                <span
                                                    style={{
                                                        fontFamily: 'monospace',
                                                        color: '#fde68a'
                                                    }}
                                                >
                                                    #{scanResult.rewardEvaluation.rewardCouponCode}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const code =
                                                    scanResult.rewardEvaluation.rewardCouponCode;
                                                handleSwitchOperationalMode(
                                                    'REWARD_REDEEM',
                                                    'REWARD_BOOTH'
                                                );
                                                setManualInput(code);
                                            }}
                                            style={{
                                                background: '#f59e0b',
                                                color: '#09090b',
                                                border: 'none',
                                                borderRadius: 10,
                                                padding: '8px 14px',
                                                fontWeight: 900,
                                                fontSize: 12.5,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🎁 Ir a Canjear Cupón →
                                        </button>
                                    </div>
                                )}

                            {/* Ficha Técnica de la Operación */}
                            <div
                                style={{
                                    marginTop: 14,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))',
                                    gap: 10
                                }}
                            >
                                <div
                                    style={{
                                        background: 'rgba(0,0,0,0.28)',
                                        borderRadius: 12,
                                        padding: '10px 12px'
                                    }}
                                >
                                    <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>
                                        FOLIO / CÓDIGO
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 18,
                                            fontWeight: 900,
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {scanResult.rewardCouponCode ||
                                            scanResult.ticketFolio ||
                                            scanResult.scannedInput ||
                                            'N/A'}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        background: 'rgba(0,0,0,0.28)',
                                        borderRadius: 12,
                                        padding: '10px 12px'
                                    }}
                                >
                                    <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>
                                        AFICIONADO
                                    </div>
                                    <div style={{ fontSize: 15.5, fontWeight: 900 }}>
                                        {scanResult.participantName || 'Walk-In / Directo'}
                                    </div>
                                </div>

                                {scanResult.operationType === 'POS_STORE' && (
                                    <div
                                        style={{
                                            background: 'rgba(0,0,0,0.28)',
                                            borderRadius: 12,
                                            padding: '10px 12px'
                                        }}
                                    >
                                        <div
                                            style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}
                                        >
                                            CONSUMO POS / TOTAL ACUMULADO
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 900 }}>
                                            +{formatCurrencyMxn(scanResult.transactionAmount)} →{' '}
                                            <span style={{ color: '#fde68a' }}>
                                                {formatCurrencyMxn(scanResult.newTotalSpentAmount)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {scanResult.operationType === 'ACTIVATION' && (
                                    <div
                                        style={{
                                            background: 'rgba(0,0,0,0.28)',
                                            borderRadius: 12,
                                            padding: '10px 12px'
                                        }}
                                    >
                                        <div
                                            style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}
                                        >
                                            PATROCINADOR / ACTIVACIONES
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 900 }}>
                                            {scanResult.sponsorName || scanResult.stationName} • Total:{' '}
                                            {scanResult.newCompletedActivationsCount ?? 1}
                                        </div>
                                    </div>
                                )}

                                {scanResult.operationType === 'REWARD_REDEEM' && (
                                    <div
                                        style={{
                                            background: 'rgba(0,0,0,0.28)',
                                            borderRadius: 12,
                                            padding: '10px 12px'
                                        }}
                                    >
                                        <div
                                            style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}
                                        >
                                            SOUVENIR OFICIAL
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 900 }}>
                                            {scanResult.rewardPrizeTitle ||
                                                'Vaso Conmemorativo Oficial Xolo Fest'}
                                        </div>
                                    </div>
                                )}

                                {scanResult.operationType === 'ACCESS' && (
                                    <div
                                        style={{
                                            background: 'rgba(0,0,0,0.28)',
                                            borderRadius: 12,
                                            padding: '10px 12px'
                                        }}
                                    >
                                        <div
                                            style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}
                                        >
                                            CATEGORÍA / AFORO ZONA
                                        </div>
                                        <div style={{ fontSize: 15.5, fontWeight: 900 }}>
                                            {scanResult.ticketCategory || 'GENERAL'} •{' '}
                                            {scanResult.zoneCurrentOccupancy ??
                                                currentZone.currentOccupancy}
                                            /
                                            {scanResult.zoneCapacityLimit ??
                                                currentZone.capacityLimit}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>

                {/* ====================================================================
                    7. PANEL ESPECÍFICO SEGÚN MODO OPERATIVO (POS | ACTIVATION | REDEEM)
                   ==================================================================== */}

                {/* MODO B: PUNTO DE VENTA POS TÁCTIL CON FLUJO DUAL FLEXIBLE (HU-05) */}
                {operationalMode === 'POS_STORE' && (
                    <section
                        style={{
                            background: '#141418',
                            border: '2px solid rgba(245, 158, 11, 0.45)',
                            borderRadius: 18,
                            padding: 18,
                            marginBottom: 18
                        }}
                    >
                        {/* Selector de Tienda / Stand POS */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 10,
                                marginBottom: 14
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 900, color: '#fde68a' }}>
                                    🍔 CAJA TÁCTIL DE PUNTO DE VENTA (FLUJO DUAL FLEXIBLE)
                                </div>
                                <div style={{ fontSize: 12, color: '#a1a1aa' }}>
                                    Puedes armar el carrito primero y escanear al final, o escanear
                                    primero al aficionado y cobrar después.
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    overflowX: 'auto',
                                    paddingBottom: 4,
                                    scrollSnapType: 'x mandatory',
                                    WebkitOverflowScrolling: 'touch',
                                    scrollbarWidth: 'thin'
                                }}
                            >
                                {(posStations.length > 0
                                    ? posStations
                                    : FALLBACK_STATIONS_CATALOG.filter(
                                          (s) => s.stationType === 'POS_STORE'
                                      )
                                ).map((st) => {
                                    const isSel = st.stationCode === selectedStationCode;
                                    return (
                                        <button
                                            key={st.stationCode}
                                            type="button"
                                            onClick={() =>
                                                handleSelectStation(st.stationCode, 'POS_STORE')
                                            }
                                            style={{
                                                flex: '1 0 auto',
                                                whiteSpace: 'nowrap',
                                                scrollSnapAlign: 'start',
                                                background: isSel ? '#f59e0b' : '#18181b',
                                                color: isSel ? '#09090b' : '#a1a1aa',
                                                border: isSel
                                                    ? '2px solid #fde68a'
                                                    : '1px solid rgba(255,255,255,0.08)',
                                                borderRadius: 12,
                                                padding: '9px 14px',
                                                fontWeight: 900,
                                                fontSize: 13,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {st.stationCode === 'STAND_XOLOSHOP' ? '🛍️ ' : '🌮 '}
                                            {st.stationName}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Cuadrícula de Productos Táctiles Grandes con Swipe Horizontal */}
                        {productsLoading ? (
                            <div style={{ padding: 20, textAlign: 'center', color: '#a1a1aa' }}>
                                ⏳ Cargando catálogo de productos de {selectedStationCode}...
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    overflowX: 'auto',
                                    gap: 12,
                                    paddingBottom: 8,
                                    marginBottom: 16,
                                    scrollSnapType: 'x mandatory',
                                    WebkitOverflowScrolling: 'touch',
                                    scrollbarWidth: 'thin'
                                }}
                            >
                                {stationProducts.map((prod) => {
                                    const key = String(prod.productId || prod.productName);
                                    const qty = posCart[key]?.quantity || 0;
                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                flex: '1 0 clamp(200px, 60vw, 240px)',
                                                minWidth: 200,
                                                scrollSnapAlign: 'start',
                                                background:
                                                    qty > 0
                                                        ? 'linear-gradient(145deg, rgba(245,158,11,0.18) 0%, #18181b 100%)'
                                                        : '#18181b',
                                                border:
                                                    qty > 0
                                                        ? '2px solid #f59e0b'
                                                        : '1px solid rgba(255,255,255,0.08)',
                                                borderRadius: 14,
                                                padding: '12px 14px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                gap: 10
                                            }}
                                        >
                                            <div
                                                onClick={() => handleAdjustCartProduct(prod, 1)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    <span style={{ fontSize: 28 }}>
                                                        {getProductEmoji(prod)}
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: 17,
                                                            fontWeight: 900,
                                                            color: '#34d399',
                                                            fontFamily: 'monospace'
                                                        }}
                                                    >
                                                        {formatCurrencyMxn(prod.price)}
                                                    </span>
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: 14.5,
                                                        fontWeight: 900,
                                                        color: '#ffffff'
                                                    }}
                                                >
                                                    {prod.productName}
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: '#18181b',
                                                    borderRadius: 10,
                                                    padding: '4px 6px'
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleAdjustCartProduct(prod, -1)
                                                    }
                                                    disabled={qty === 0}
                                                    style={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: 8,
                                                        border: 'none',
                                                        background:
                                                            qty > 0 ? '#dc2626' : '#27272a',
                                                        color: '#ffffff',
                                                        fontWeight: 900,
                                                        fontSize: 18,
                                                        cursor: qty > 0 ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    −
                                                </button>
                                                <span
                                                    style={{
                                                        fontSize: 15,
                                                        fontWeight: 900,
                                                        fontFamily: 'monospace',
                                                        color: qty > 0 ? '#fde68a' : '#a1a1aa'
                                                    }}
                                                >
                                                    {qty}x
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAdjustCartProduct(prod, 1)}
                                                    style={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: 8,
                                                        border: 'none',
                                                        background: '#10b981',
                                                        color: '#09090b',
                                                        fontWeight: 900,
                                                        fontSize: 18,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Bloque de Monto Libre Opcional + Resumen de Carrito POS */}
                        <div
                            style={{
                                background: '#09090b',
                                border: '1px solid rgba(255,255,255,0.14)',
                                borderRadius: 14,
                                padding: 14,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                gap: 14,
                                alignItems: 'center'
                            }}
                        >
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <div style={{ width: 130 }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color: '#a1a1aa',
                                            marginBottom: 4
                                        }}
                                    >
                                        Monto Libre ($ MXN)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="10"
                                        value={customAmount}
                                        onChange={(e) => setCustomAmount(e.target.value)}
                                        placeholder="$0"
                                        style={{
                                            width: '100%',
                                            background: '#18181b',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            borderRadius: 10,
                                            padding: '9px 10px',
                                            color: '#ffffff',
                                            fontSize: 14,
                                            fontWeight: 800,
                                            fontFamily: 'monospace'
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: 160 }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color: '#a1a1aa',
                                            marginBottom: 4
                                        }}
                                    >
                                        Concepto Adicional (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        value={customConcept}
                                        onChange={(e) => setCustomConcept(e.target.value)}
                                        placeholder="Ej. Combo Familiar / Propina"
                                        style={{
                                            width: '100%',
                                            background: '#18181b',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            borderRadius: 10,
                                            padding: '9px 10px',
                                            color: '#ffffff',
                                            fontSize: 13,
                                            fontWeight: 600
                                        }}
                                    />
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 10
                                }}
                            >
                                <div>
                                    <div style={{ fontSize: 11.5, color: '#a1a1aa', fontWeight: 700 }}>
                                        TOTAL EN CARRITO ({posCartItemsList.length} productos):
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 24,
                                            fontWeight: 900,
                                            color: '#34d399',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {formatCurrencyMxn(posGrandTotal)}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    {posGrandTotal > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleClearPosCart}
                                            style={{
                                                background: 'rgba(255,255,255,0.08)',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                color: '#d4d4d8',
                                                borderRadius: 10,
                                                padding: '10px 12px',
                                                fontSize: 12,
                                                fontWeight: 800,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🗑️ Vaciar
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        disabled={scanningInProgress || posGrandTotal <= 0}
                                        onClick={() =>
                                            executePosPurchase(
                                                identifiedParticipant?.ticketFolio || manualInput,
                                                'MANUAL'
                                            )
                                        }
                                        style={{
                                            background:
                                                posGrandTotal > 0
                                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                    : '#27272a',
                                            color: posGrandTotal > 0 ? '#09090b' : '#71717a',
                                            border: 'none',
                                            borderRadius: 12,
                                            padding: '12px 16px',
                                            fontWeight: 900,
                                            fontSize: 13.5,
                                            cursor:
                                                posGrandTotal > 0 && !scanningInProgress
                                                    ? 'pointer'
                                                    : 'not-allowed'
                                        }}
                                    >
                                        💳 Registrar Consumo ({formatCurrencyMxn(posGrandTotal)}) y
                                        Estampar Sello
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* MODO C: ACTIVACIÓN DE PATROCINADOR CON PÍLDORAS RÁPIDAS (HU-05) */}
                {operationalMode === 'ACTIVATION' && (
                    <section
                        style={{
                            background: '#141418',
                            border: '2px solid rgba(220, 38, 38, 0.5)',
                            borderRadius: 18,
                            padding: 18,
                            marginBottom: 18
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 10,
                                marginBottom: 14
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 900, color: '#fca5a5' }}>
                                    🎯 ACTIVACIÓN DE PATROCINADOR (CANDADO MANDATORY_ACTIVATION)
                                </div>
                                <div style={{ fontSize: 12, color: '#a1a1aa' }}>
                                    Elige un resultado rápido con 1 toque y escanea o sella el
                                    Pasaporte Digital del aficionado.
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    overflowX: 'auto',
                                    paddingBottom: 4,
                                    scrollSnapType: 'x mandatory',
                                    WebkitOverflowScrolling: 'touch',
                                    scrollbarWidth: 'thin'
                                }}
                            >
                                {(activationStations.length > 0
                                    ? activationStations
                                    : FALLBACK_STATIONS_CATALOG.filter(
                                          (s) => s.stationType === 'ACTIVATION'
                                      )
                                ).map((st) => {
                                    const isSel = st.stationCode === selectedStationCode;
                                    return (
                                        <button
                                            key={st.stationCode}
                                            type="button"
                                            onClick={() =>
                                                handleSelectStation(st.stationCode, 'ACTIVATION')
                                            }
                                            style={{
                                                flex: '1 0 auto',
                                                whiteSpace: 'nowrap',
                                                scrollSnapAlign: 'start',
                                                background: isSel ? '#dc2626' : '#18181b',
                                                color: isSel ? '#ffffff' : '#a1a1aa',
                                                border: isSel
                                                    ? '2px solid #fca5a5'
                                                    : '1px solid rgba(255,255,255,0.08)',
                                                borderRadius: 12,
                                                padding: '9px 14px',
                                                fontWeight: 900,
                                                fontSize: 13,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {st.stationCode === 'ACT_CALIENTE' ? '⚽ ' : '🚗 '}
                                            {st.stationName}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Píldoras Táctiles de Resultados Rápidos con Swipe Horizontal */}
                        <div
                            style={{
                                display: 'flex',
                                overflowX: 'auto',
                                gap: 10,
                                paddingBottom: 6,
                                marginBottom: 14,
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'thin'
                            }}
                        >
                            {currentActivationPresets.map((preset) => {
                                const isChosen = selectedPresetId === preset.id;
                                return (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        onClick={() => {
                                            setSelectedPresetId(preset.id);
                                            setActivationTitleInput(preset.activationName);
                                            setActivationResultInput(preset.resultSummary);
                                            setActivationScoreInput(String(preset.scoreValue));
                                        }}
                                        style={{
                                            flex: '1 0 clamp(220px, 60vw, 260px)',
                                            minWidth: 220,
                                            scrollSnapAlign: 'start',
                                            background: isChosen
                                                ? 'rgba(220, 38, 38, 0.28)'
                                                : '#18181b',
                                            border: isChosen
                                                ? '2px solid #ef4444'
                                                : '1px solid rgba(255,255,255,0.08)',
                                            color: '#ffffff',
                                            borderRadius: 12,
                                            padding: '12px 14px',
                                            textAlign: 'left',
                                            fontWeight: 800,
                                            fontSize: 13,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {preset.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Campos Editables de Resultado Personalizado y Puntaje */}
                        <div
                            style={{
                                background: '#09090b',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 14,
                                padding: 14,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 10,
                                alignItems: 'flex-end'
                            }}
                        >
                            <div style={{ flex: 2, minWidth: 200 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: '#a1a1aa',
                                        marginBottom: 4
                                    }}
                                >
                                    Resultado / Premio Obtenido en la Dinámica
                                </label>
                                <input
                                    type="text"
                                    value={activationResultInput}
                                    onChange={(e) => setActivationResultInput(e.target.value)}
                                    style={{
                                        width: '100%',
                                        background: '#18181b',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 10,
                                        padding: '10px 12px',
                                        color: '#ffffff',
                                        fontSize: 13.5,
                                        fontWeight: 700
                                    }}
                                />
                            </div>

                            <div style={{ width: 110 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: '#a1a1aa',
                                        marginBottom: 4
                                    }}
                                >
                                    Puntaje (Pts)
                                </label>
                                <input
                                    type="number"
                                    value={activationScoreInput}
                                    onChange={(e) => setActivationScoreInput(e.target.value)}
                                    style={{
                                        width: '100%',
                                        background: '#18181b',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 10,
                                        padding: '10px 12px',
                                        color: '#ffffff',
                                        fontSize: 14,
                                        fontWeight: 800,
                                        fontFamily: 'monospace'
                                    }}
                                />
                            </div>

                            <button
                                type="button"
                                disabled={scanningInProgress}
                                onClick={() =>
                                    executeSponsorActivation(
                                        identifiedParticipant?.ticketFolio || manualInput,
                                        'MANUAL'
                                    )
                                }
                                style={{
                                    background:
                                        'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: 12,
                                    padding: '11px 18px',
                                    fontWeight: 900,
                                    fontSize: 13.5,
                                    cursor: 'pointer'
                                }}
                            >
                                🎯 Sellar Activación de Marca (
                                {identifiedParticipant?.ticketFolio || manualInput || 'XOLO-0001'})
                            </button>
                        </div>
                    </section>
                )}

                {/* MODO D: CANJE DE SOUVENIR FÍSICO (#CANJE-XXXXXX) (HU-05) */}
                {operationalMode === 'REWARD_REDEEM' && (
                    <section
                        style={{
                            background: 'linear-gradient(145deg, #1c1608 0%, #141418 100%)',
                            border: '2px solid #eab308',
                            borderRadius: 18,
                            padding: 18,
                            marginBottom: 18
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 10
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 15.5, fontWeight: 900, color: '#fde68a' }}>
                                    🎁 MÓDULO DE CARPA DE PREMIOS — VALIDACIÓN Y ENTREGA DE SOUVENIR
                                    (#CANJE)
                                </div>
                                <div style={{ fontSize: 12.5, color: '#d4d4d8', marginTop: 3 }}>
                                    Escanea el QR del Cupón Dorado (
                                    <code style={{ color: '#fde68a' }}>#CANJE-XXXXXX</code>) o el
                                    folio del boleto (<code style={{ color: '#fde68a' }}>XOLO-XXXX</code>)
                                    para autorizar la entrega física del{' '}
                                    <strong>
                                        {config?.rewardPrizeTitle ||
                                            'Vaso Conmemorativo Oficial Xolo Fest'}
                                    </strong>{' '}
                                    e impedir doble canje.
                                </div>
                            </div>

                            <button
                                type="button"
                                disabled={scanningInProgress}
                                onClick={() =>
                                    executeRewardRedemption(
                                        identifiedParticipant?.rewardCouponCode ||
                                            identifiedParticipant?.ticketFolio ||
                                            manualInput,
                                        'MANUAL'
                                    )
                                }
                                style={{
                                    background:
                                        'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: 12,
                                    padding: '12px 18px',
                                    fontWeight: 900,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.35)'
                                }}
                            >
                                🎁 Validar y Entregar Souvenir Físico
                            </button>
                        </div>
                    </section>
                )}

                {/* 8. PANEL OPERATIVO DIVIDIDO: CÁMARA QR + CAPTURA MANUAL / KEYBOARD WEDGE */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
                        gap: 16,
                        marginBottom: 22
                    }}
                >
                    {/* Columna Izquierda: Lector de Cámara HTML5-QRCODE */}
                    <div
                        style={{
                            background: '#141418',
                            border: '1.5px solid rgba(255,255,255,0.14)',
                            borderRadius: 18,
                            padding: 18
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 12,
                                flexWrap: 'wrap',
                                gap: 8
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 900 }}>
                                    📷 Lector Óptico QR (Cámara Móvil / Tablet)
                                </div>
                                <div style={{ fontSize: 12, color: '#a1a1aa' }}>
                                    Debounce automático de 2.5s • Conectado al modo{' '}
                                    <strong style={{ color: '#fde68a' }}>{operationalMode}</strong>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 6 }}>
                                {!cameraActive ? (
                                    <button
                                        type="button"
                                        onClick={() => startCameraScanner(cameraFacingMode)}
                                        style={{
                                            background: '#10b981',
                                            color: '#09090b',
                                            border: 'none',
                                            borderRadius: 10,
                                            padding: '8px 14px',
                                            fontWeight: 900,
                                            fontSize: 13,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        ▶ Encender Cámara
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleSwitchCameraFacing}
                                            style={{
                                                background: 'rgba(255,255,255,0.12)',
                                                color: '#ffffff',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                borderRadius: 10,
                                                padding: '8px 11px',
                                                fontWeight: 800,
                                                fontSize: 12,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🔄{' '}
                                            {cameraFacingMode === 'environment'
                                                ? 'Frontal'
                                                : 'Trasera'}
                                        </button>

                                        {torchSupported && (
                                            <button
                                                type="button"
                                                onClick={handleToggleTorch}
                                                style={{
                                                    background: torchOn
                                                        ? '#f59e0b'
                                                        : 'rgba(255,255,255,0.12)',
                                                    color: torchOn ? '#09090b' : '#ffffff',
                                                    border: 'none',
                                                    borderRadius: 10,
                                                    padding: '8px 11px',
                                                    fontWeight: 800,
                                                    fontSize: 12,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                🔦 Linterna
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={stopCameraScanner}
                                            style={{
                                                background: '#dc2626',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: 10,
                                                padding: '8px 12px',
                                                fontWeight: 900,
                                                fontSize: 12,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ⏹ Apagar
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {cameraError && (
                            <div
                                style={{
                                    background: 'rgba(220, 38, 38, 0.16)',
                                    border: '1px solid #ef4444',
                                    color: '#fecaca',
                                    borderRadius: 10,
                                    padding: '10px 12px',
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    marginBottom: 12
                                }}
                            >
                                ⚠️ {cameraError}
                            </div>
                        )}

                        <div
                            style={{
                                position: 'relative',
                                minHeight: 250,
                                background: '#09090b',
                                border: cameraActive
                                    ? '2px solid #10b981'
                                    : '2px dashed rgba(255,255,255,0.18)',
                                borderRadius: 14,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <div
                                id="xf-qr-reader-viewport"
                                style={{
                                    width: '100%',
                                    maxWidth: 360
                                }}
                            />
                            {!cameraActive && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 20,
                                        textAlign: 'center'
                                    }}
                                >
                                    <div style={{ fontSize: 38, marginBottom: 8 }}>📱</div>
                                    <div style={{ fontWeight: 800, fontSize: 14, color: '#e4e4e7' }}>
                                        Cámara en espera para ahorro de batería
                                    </div>
                                    <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 4 }}>
                                        Presiona &ldquo;Encender Cámara&rdquo; o utiliza directamente
                                        tu pistola lectora USB/Bluetooth en cualquier momento.
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Columna Derecha: Captura Manual, Identificación Dual y Pistola USB/BT */}
                    <div
                        style={{
                            background: '#141418',
                            border: '1.5px solid rgba(255,255,255,0.14)',
                            borderRadius: 18,
                            padding: 18,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 10,
                                    flexWrap: 'wrap',
                                    gap: 6
                                }}
                            >
                                <div style={{ fontSize: 15, fontWeight: 900 }}>
                                    ⌨️ Folio / Token QR / Cupón #CANJE
                                </div>
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        padding: '3px 8px',
                                        borderRadius: 6,
                                        background: 'rgba(16, 185, 129, 0.16)',
                                        color: '#6ee7b7'
                                    }}
                                >
                                    Estación: {selectedStationCode}
                                </span>
                            </div>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleUniversalScanTrigger(manualInput, 'MANUAL');
                                }}
                            >
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#a1a1aa',
                                        marginBottom: 6
                                    }}
                                >
                                    Escribe o escanea Folio (XOLO-0001), Token QR o Cupón
                                    (#CANJE-XXXXXX):
                                </label>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        marginBottom: 10,
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={manualInput}
                                        onChange={(e) =>
                                            setManualInput(e.target.value.toUpperCase())
                                        }
                                        placeholder="XOLO-0001 o #CANJE-..."
                                        style={{
                                            flex: 1,
                                            minWidth: 170,
                                            background: '#09090b',
                                            border: '2px solid rgba(255,255,255,0.22)',
                                            borderRadius: 12,
                                            padding: '12px 14px',
                                            color: '#ffffff',
                                            fontSize: 15.5,
                                            fontWeight: 900,
                                            fontFamily: 'monospace'
                                        }}
                                    />
                                    <button
                                        type="submit"
                                        disabled={scanningInProgress || !manualInput.trim()}
                                        style={{
                                            background:
                                                operationalMode === 'POS_STORE'
                                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                    : operationalMode === 'REWARD_REDEEM'
                                                    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                                                    : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                            color:
                                                operationalMode === 'POS_STORE'
                                                    ? '#09090b'
                                                    : '#ffffff',
                                            border: 'none',
                                            borderRadius: 12,
                                            padding: '12px 16px',
                                            fontWeight: 900,
                                            fontSize: 13.5,
                                            cursor: scanningInProgress ? 'wait' : 'pointer'
                                        }}
                                    >
                                        {scanningInProgress
                                            ? '⏳...'
                                            : operationalMode === 'ACCESS'
                                            ? 'Validar Acceso'
                                            : operationalMode === 'POS_STORE'
                                            ? posGrandTotal > 0
                                                ? `Cobrar ${formatCurrencyMxn(posGrandTotal)}`
                                                : '👤 Identificar Cliente'
                                            : operationalMode === 'ACTIVATION'
                                            ? '🎯 Sellar Activación'
                                            : '🎁 Canjear Souvenir'}
                                    </button>
                                </div>
                            </form>

                            {/* Botón auxiliar para Identificar Primero en cualquier modo (Flujo Dual Orden 2) */}
                            {operationalMode !== 'ACCESS' && (
                                <div style={{ marginBottom: 12 }}>
                                    <button
                                        type="button"
                                        disabled={lookupLoading || !manualInput.trim()}
                                        onClick={() => executeParticipantLookup(manualInput)}
                                        style={{
                                            width: '100%',
                                            background: 'rgba(16, 185, 129, 0.14)',
                                            border: '1px solid #10b981',
                                            color: '#a7f3d0',
                                            borderRadius: 10,
                                            padding: '8px 12px',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {lookupLoading
                                            ? '⏳ Consultando padrón...'
                                            : '🔍 Identificar y Fijar Aficionado Primero (Flujo Dual Orden 2)'}
                                    </button>
                                </div>
                            )}

                            {/* Píldoras Rápidas de Boletos de Muestra */}
                            <div style={{ marginBottom: 14 }}>
                                <div
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 800,
                                        color: '#a1a1aa',
                                        marginBottom: 6
                                    }}
                                >
                                    🎟️ Boletos de Muestra Rápida (1 Toque):
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {QUICK_SAMPLE_FOLIOS.map((item) => (
                                        <button
                                            key={item.folio}
                                            type="button"
                                            onClick={() => {
                                                setManualInput(item.folio);
                                                handleUniversalScanTrigger(item.folio, 'MANUAL');
                                            }}
                                            style={{
                                                background:
                                                    manualInput === item.folio
                                                        ? 'rgba(245, 158, 11, 0.25)'
                                                        : 'rgba(255,255,255,0.06)',
                                                border:
                                                    manualInput === item.folio
                                                        ? '1.5px solid #f59e0b'
                                                        : '1px solid rgba(255,255,255,0.16)',
                                                color: '#ffffff',
                                                borderRadius: 8,
                                                padding: '6px 10px',
                                                fontSize: 12,
                                                fontWeight: 800,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                background: '#09090b',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 14,
                                padding: '12px 14px'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 6,
                                    flexWrap: 'wrap',
                                    gap: 6
                                }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 900, color: '#34d399' }}>
                                    🔫 Pistola Láser USB / Bluetooth (Keyboard Wedge)
                                </span>
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        background: semaphore.bg,
                                        color: semaphore.text
                                    }}
                                >
                                    {semaphore.label}
                                </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#a1a1aa', lineHeight: 1.4 }}>
                                Ráfagas rápidas terminadas en <code style={{ color: '#fde68a' }}>Enter</code>{' '}
                                ejecutan automáticamente la operación del modo{' '}
                                <strong>{operationalMode}</strong>.
                                {wedgeLastBurst && (
                                    <span
                                        style={{
                                            marginLeft: 6,
                                            color: '#fde68a',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        [Última lectura: {wedgeLastBurst}]
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 9. BITÁCORA EN VIVO DE LA ESTACIÓN (AUDITORÍA CON FIRMA DE OPERADOR) */}
                <section
                    style={{
                        background: '#141418',
                        border: '1.5px solid rgba(255,255,255,0.14)',
                        borderRadius: 18,
                        padding: 18
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 10,
                            marginBottom: 12
                        }}
                    >
                        <div>
                            <h3 style={{ fontSize: 16, fontWeight: 900, margin: 0 }}>
                                📋 Bitácora de Auditoría en Vivo (Accesos, POS, Activaciones y
                                Canjes)
                            </h3>
                            <div style={{ fontSize: 12, color: '#a1a1aa' }}>
                                Cada operación registra hora exacta, estación, acción, estatus y
                                firma JWT del operador.
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button
                                type="button"
                                onClick={() => setAuditLogsOpen((prev) => !prev)}
                                style={{
                                    background: auditLogsOpen
                                        ? 'rgba(245, 158, 11, 0.22)'
                                        : 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    color: '#ffffff',
                                    borderRadius: 8,
                                    padding: '6px 11px',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                }}
                            >
                                {auditLogsOpen
                                    ? '▲ Ocultar Bitácora'
                                    : `▼ Ver Bitácora (${recentLogs.length})`}
                            </button>

                            {auditLogsOpen && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setFilterLogsByStation((prev) => !prev)}
                                        style={{
                                            background: filterLogsByStation
                                                ? 'rgba(245, 158, 11, 0.2)'
                                                : 'rgba(255, 255, 255, 0.08)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            color: '#ffffff',
                                            borderRadius: 8,
                                            padding: '6px 11px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {filterLogsByStation
                                            ? `📍 Filtrado: ${selectedStationCode}`
                                            : '🌐 Todas'}
                                    </button>

                                    <button
                                        type="button"
                                        disabled={logsLoading}
                                        onClick={() => refreshLogsAndZones()}
                                        style={{
                                            background: 'rgba(255,255,255,0.08)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            color: '#ffffff',
                                            borderRadius: 8,
                                            padding: '6px 11px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {logsLoading ? '⏳...' : '🔄 Actualizar'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {auditLogsOpen && (
                        recentLogs.length === 0 ? (
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '24px 12px',
                                    color: '#a1a1aa',
                                    fontSize: 13
                                }}
                            >
                                Aún no hay operaciones registradas en esta sesión. Utiliza el escáner o el botón flotante de pruebas demo para auditar.
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: 12.5
                                    }}
                                >
                                    <thead>
                                        <tr
                                            style={{
                                                borderBottom: '1px solid rgba(255,255,255,0.15)',
                                                color: '#a1a1aa',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <th style={{ padding: '8px 10px' }}>Hora</th>
                                            <th style={{ padding: '8px 10px' }}>Folio</th>
                                            <th style={{ padding: '8px 10px' }}>Participante</th>
                                            <th style={{ padding: '8px 10px' }}>Estación</th>
                                            <th style={{ padding: '8px 10px' }}>Acción / Detalle</th>
                                            <th style={{ padding: '8px 10px' }}>Resultado</th>
                                            <th style={{ padding: '8px 10px' }}>Operador</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentLogs.map((log) => {
                                            const isSuccess =
                                                log.status === 'GRANTED' ||
                                                log.status === 'PURCHASE_RECORDED' ||
                                                log.status === 'ACTIVATION_RECORDED' ||
                                                log.status === 'REWARD_REDEEMED' ||
                                                log.status === 'SUCCESS';
                                            const isWarning =
                                                log.status === 'DENIED_ANTI_PASSBACK' ||
                                                log.status === 'ALREADY_REDEEMED' ||
                                                log.status === 'DIGITAL_BADGE_ONLY';
                                            const badgeBg = isSuccess
                                                ? 'rgba(16, 185, 129, 0.2)'
                                                : isWarning
                                                ? 'rgba(245, 158, 11, 0.22)'
                                                : 'rgba(220, 38, 38, 0.22)';
                                            const badgeColor = isSuccess
                                                ? '#6ee7b7'
                                                : isWarning
                                                ? '#fde68a'
                                                : '#fca5a5';

                                            return (
                                                <tr
                                                    key={
                                                        log.id ||
                                                        `${log.ticketFolio}-${log.timestampUtc}`
                                                    }
                                                    style={{
                                                        borderBottom: '1px solid rgba(255,255,255,0.07)'
                                                    }}
                                                >
                                                    <td
                                                        style={{
                                                            padding: '9px 10px',
                                                            fontFamily: 'monospace',
                                                            color: '#d4d4d8'
                                                        }}
                                                    >
                                                        {formatTimeLocal(log.timestampUtc)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '9px 10px',
                                                            fontFamily: 'monospace',
                                                            fontWeight: 900,
                                                            color: '#ffffff'
                                                        }}
                                                    >
                                                        {log.ticketFolio}
                                                    </td>
                                                    <td style={{ padding: '9px 10px', color: '#e4e4e7' }}>
                                                        {log.participantName || 'Walk-In / Directo'}
                                                    </td>
                                                    <td style={{ padding: '9px 10px', color: '#d4d4d8' }}>
                                                        {log.stationCode}{' '}
                                                        {log.zoneCode ? `(${log.zoneCode})` : ''}
                                                    </td>
                                                    <td style={{ padding: '9px 10px', fontWeight: 700 }}>
                                                        {log.actionName || log.stationName}
                                                    </td>
                                                    <td style={{ padding: '9px 10px' }}>
                                                        <span
                                                            style={{
                                                                background: badgeBg,
                                                                color: badgeColor,
                                                                padding: '3px 8px',
                                                                borderRadius: 6,
                                                                fontWeight: 900,
                                                                fontSize: 11,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        >
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '9px 10px',
                                                            fontFamily: 'monospace',
                                                            color: '#fde68a',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {log.operatorUsername || 'staff'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </section>
            </main>

            {/* Botón Flotante de Acceso Rápido al Centro de Pruebas Demo (1 Toque) */}
            <button
                type="button"
                onClick={() => setDemoDrawerOpen(true)}
                style={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#09090b',
                    border: 'none',
                    borderRadius: 999,
                    padding: '11px 18px',
                    fontWeight: 900,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 8px 25px rgba(245, 158, 11, 0.45)',
                    cursor: 'pointer',
                    zIndex: 50
                }}
                title="Abrir Panel de Simuladores Demo (HU-04 / HU-05)"
            >
                <i className="bi bi-tools"></i>
                <span>Simuladores Demo</span>
            </button>
        </div>
    );
};

export default XoloFestScanner;
